import * as THREE from 'three';
import React from 'react';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {productClamping} from 'components/customer/RoomPlanner/helpers/boundaries/clamping';
import {
    computeWorldBoundsExcludingAnimatedDoorParts,
    getGroupWidth,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {setArrowHighPriority} from 'components/customer/RoomPlanner/helpers/createRoomObjects';
import {getRoomDimensionsComputed} from 'components/customer/RoomPlanner/helpers/getComputedDimension';
import {
    CabinetGroup,
    hasDoors,
} from 'components/customer/RoomPlanner/helpers/doorDetection';

/**
 * Gets the scale factor from dimensions
 * @return {number} The scale factor (typically 0.2)
 */
const getScale = (): number => {
    const dimensions = getRoomDimensionsComputed();
    return dimensions.scale;
};

// Configuration: Maximum distance (in scaled units) to show the distance measurement line
// To convert to actual millimeters: distance / scale
// Example: 200 units = 1000mm (100cm) when scale is 0.2
export const MAX_DISTANCE_TO_SHOW_LINE = 180;

// Configuration: Minimum distance (in scaled units) to show the line
// This prevents showing "0mm" or very small values
// Example: 0.01 units = 0.05mm actual distance when scale is 0.2
// Set to very small value to allow showing from 1mm (0.2 scaled units)
export const MIN_DISTANCE_TO_SHOW_LINE = 0.01;

// Configuration: Tolerance for boundary detection when hiding side buttons
// This is the distance threshold (in scaled units) from the wall boundary
// Lower values = button hides when closer to wall, Higher values = button hides earlier
// Example: 15 units = 75mm actual distance (15 / scale = 75mm) when scale is 0.2
export const WALL_BOUNDARY_TOLERANCE = 3.8;

// Configuration: Maximum position difference threshold for hiding side buttons
// If the clamped position differs from simulated position by more than this value,
// the button will be hidden (indicating wall proximity adjustment)
// Lower values = button hides with smaller adjustments, Higher values = allows more adjustment
// Example: 20 units = 100mm actual distance (20 / scale = 100mm) when scale is 0.2
export const MAX_POSITION_DIFF_THRESHOLD = 20;

/**
 * Finds the cabinet that has positioning flags set (addOnLeftSide or addOnRightSide)
 * @param {CabinetMesh[]} existingCabinets - Array of existing cabinet meshes in the scene
 * @return {CabinetMesh | undefined} The target cabinet with positioning flags, or undefined
 */
export const findCabinetWithPositioningFlag = (
    existingCabinets: CabinetMesh[]
): CabinetMesh | undefined => {
    return existingCabinets.find(
        (cab) => cab.userData.addOnLeftSide || cab.userData.addOnRightSide
    );
};

/**
 * Gets the side (left or right) from the positioning flags on a cabinet
 * @param {CabinetMesh} cabinet - The cabinet with positioning flags
 * @return {'left' | 'right'} The side to position the new cabinet
 */
export const getSideFromCabinet = (cabinet: CabinetMesh): 'left' | 'right' => {
    return cabinet.userData.addOnRightSide ? 'right' : 'left';
};

/**
 * Sets positioning flags on a cabinet for side button functionality
 * @param {CabinetMesh} cabinet - The cabinet to set flags on
 * @param {'left' | 'right'} side - The side to position new cabinets
 */
export const setCabinetPositioningFlags = (
    cabinet: CabinetMesh,
    side: 'left' | 'right'
): void => {
    if (side === 'left') {
        cabinet.userData.addOnLeftSide = true;
        cabinet.userData.addOnRightSide = false;
    } else {
        cabinet.userData.addOnRightSide = true;
        cabinet.userData.addOnLeftSide = false;
    }
};

/**
 * Clears positioning flags from a cabinet
 * @param {CabinetMesh} cabinet - The cabinet to clear flags from
 */
export const clearCabinetPositioningFlags = (cabinet: CabinetMesh): void => {
    cabinet.userData.addOnLeftSide = false;
    cabinet.userData.addOnRightSide = false;
};

/** Target cabinet userData.values used for depth/width calculations */
type TargetCabinetValues = {
    cabinet_width_depth?: number | string;
    cabinet_length_depth?: number | string;
    width_depth?: number | string;
    length_depth?: number | string;
    cabinet_left_depth?: number | string;
    cabinet_right_depth?: number | string;
    cabinet_width?: number | string;
    cabinet_length?: number | string;
    width1?: number | string;
    width2?: number | string;
    cabinet_depth?: number | string;
    cabinet_ext_thickness?: number | string;
};

type CabinetDirection = 'front' | 'right' | 'back' | 'left';

/**
 * Map `normal` to `front` so snap math matches front-facing cabinets.
 * @param {string | undefined} direction - Target cabinet `userData.currentDirection`.
 * @return {CabinetDirection} Direction key used by placement helpers.
 */
const normalizeCabinetDirectionForPlacement = (
    direction: string | undefined
): CabinetDirection => {
    if (direction === 'normal') return 'front';
    if (
        direction === 'front' ||
        direction === 'right' ||
        direction === 'back' ||
        direction === 'left'
    ) {
        return direction;
    }
    return 'front';
};

/**
 * Converts a dimension value to scaled units.
 * @param {number | string | undefined} value - Raw dimension value
 * @param {number} scale - Scale factor
 * @return {number} Value in scaled units
 */
function getScaledNum(
    value: number | string | undefined,
    scale: number
): number {
    return Number(value ?? 0) * scale;
}

/**
 * Resolves the left depth of an L-shaped cabinet in millimeters
 * @param {TargetCabinetValues} values - The values of the cabinet
 * @return {number} The left depth in millimeters
 */
export function resolveLShapedLeftDepthMm(values: TargetCabinetValues): number {
    return Number(
        values.cabinet_left_depth ??
            values.cabinet_width_depth ??
            values.width_depth ??
            0
    );
}

/**
 * Resolves the right depth of an L-shaped cabinet in millimeters
 * @param {TargetCabinetValues} values - The values of the cabinet
 * @return {number} The right depth in millimeters
 */
export function resolveLShapedRightDepthMm(
    values: TargetCabinetValues
): number {
    return Number(
        values.cabinet_right_depth ??
            values.cabinet_length_depth ??
            values.length_depth ??
            0
    );
}

/**
 * Scales the left depth of an L-shaped cabinet in millimeters
 * @param {TargetCabinetValues} values - The values of the cabinet
 * @param {number} scale - The scale factor
 * @return {number} The left depth in scaled units
 */
function getScaledLShapedLeftDepth(
    values: TargetCabinetValues,
    scale: number
): number {
    return resolveLShapedLeftDepthMm(values) * scale;
}

/**
 * Scales the right depth of an L-shaped cabinet in millimeters
 * @param {TargetCabinetValues} values - The values of the cabinet
 * @param {number} scale - The scale factor
 * @return {number} The right depth in scaled units
 */
function getScaledLShapedRightDepth(
    values: TargetCabinetValues,
    scale: number
): number {
    return resolveLShapedRightDepthMm(values) * scale;
}

/**
 * Syncs the depth aliases on an L-shaped cabinet
 * @param {CabinetMesh} cabinet - The cabinet to sync the depth aliases on
 * @return {void}
 */
export const syncLShapedDepthAliasesOnCabinet = (
    cabinet: CabinetMesh
): void => {
    if (!cabinet.userData.isLShapedProduct) return;

    const values = (cabinet.userData.values ?? {}) as TargetCabinetValues;
    const leftDepth = resolveLShapedLeftDepthMm(values);
    const rightDepth = resolveLShapedRightDepthMm(values);

    Object.assign(values, {
        cabinet_left_depth: leftDepth,
        cabinet_right_depth: rightDepth,
        cabinet_width_depth: leftDepth,
        cabinet_length_depth: rightDepth,
        width_depth: Number(values.width_depth ?? leftDepth),
        length_depth: Number(values.length_depth ?? rightDepth),
    });
};

/** Context passed to position calculators for snapped placement */
interface SnappedPositionContext {
    targetBox: THREE.Box3;
    targetValues: TargetCabinetValues;
    scale: number;
    positionY: number;
    positionX: number;
    positionZ: number;
    width: number;
    height: number;
    doorThickness: number;
    fillerFrontDepth: number;
    isPanelProduct: boolean;
    isVerminProduct: boolean;
    isFillerProduct: boolean;
    isTargetCornerOrLShaped: boolean;
    targetCabinet: CabinetMesh;
    depth: number;
    isUpperProduct: boolean;
}

/**
 * Aligns panel/filler/vermin rotation and orientation to the target cabinet.
 * @param {CabinetMesh} targetCabinet - Cabinet to align to
 * @param {CabinetMesh} newCabinet - Panel/filler/vermin cabinet to rotate
 * @param {'left' | 'right'} side - Placement side (left/right)
 * @param {boolean} isTargetCornerOrLShaped - Whether target is L-shaped/corner
 * @param {CabinetDirection} cabinetDirection - Target cabinet facing direction
 * @return {void}
 */
function applyAccessoryRotation(
    targetCabinet: CabinetMesh,
    newCabinet: CabinetMesh,
    side: 'left' | 'right',
    isTargetCornerOrLShaped: boolean,
    cabinetDirection: CabinetDirection
): void {
    const facing = normalizeCabinetDirectionForPlacement(cabinetDirection);
    const targetRotationZ = targetCabinet.rotation.z;
    const HALF_PI = Math.PI / 2;

    // Rotation is set directly via PI values instead of calling rotateLeft/rotateRight,
    // because those functions trigger side effects (position clamping, overlap revalidation,
    // auto-save) that are unnecessary during programmatic placement — the position is
    // calculated separately by calculateSnappedPosition afterward.
    //
    // Rotation reference (from renderCabinetGroup.ts):
    //   rotateLeft()  = rotation.z += π/2,  direction: front → left
    //   rotateRight() = rotation.z -= π/2,  direction: front → right
    //
    // Corner/L-shaped cabinets need the accessory rotated to align with the
    // perpendicular wing of the L-shape, depending on which side it's placed on.
    if (isTargetCornerOrLShaped && side === 'left') {
        if (facing === 'front') {
            // +π/2 (equivalent to 1× rotateLeft): face the accessory along the left wing
            newCabinet.rotation.z += HALF_PI;
            newCabinet.userData.currentDirection = 'left';
        } else if (facing !== 'right') {
            newCabinet.rotation.z = targetRotationZ;
        }
    } else if (isTargetCornerOrLShaped && side === 'right') {
        if (facing === 'back') {
            // -π/2 (equivalent to 1× rotateRight): face the accessory along the right wing
            newCabinet.rotation.z -= HALF_PI;
            newCabinet.userData.currentDirection = 'right';
        } else if (facing === 'left') {
            // -π (equivalent to 2× rotateRight): flip 180° for left-facing corner placement
            newCabinet.rotation.z -= Math.PI;
            newCabinet.userData.currentDirection = 'back';
        } else if (facing === 'right') {
            newCabinet.rotation.z = targetRotationZ;
            newCabinet.userData.currentDirection = facing;
        }
    } else {
        // Non-corner cabinets: match the target cabinet's rotation directly
        newCabinet.rotation.z = targetRotationZ;
        newCabinet.userData.currentDirection = facing;
    }

    newCabinet.rotation.x = targetCabinet.rotation.x;
    newCabinet.rotation.y = targetCabinet.rotation.y;
    newCabinet.updateMatrixWorld(true);
}

/** Small front-face nudge used when aligning fillers to door/carcase faces. */
const FILLER_FRONT_NUDGE = 0.4;
/** Historical panel offset for rotated (non-front) facings when doors exist. */
const ROTATED_PANEL_DOOR_OFFSET = 4;

/**
 * Shared side-contact + front-face alignment offsets for accessory placement.
 * Front facing keeps historical door-thickness nudges; rotated facings use +4
 * for panels. Upper fillers with doors keep a 0 front offset.
 * @param {SnappedPositionContext} ctx - Snapped position context
 * @param {'front' | 'rotated'} facing - Front uses doorThickness; rotated uses +4 for panels
 * @return {object} Side offsets, front-align offset, and L-wing depths
 */
function getAccessoryPlacementOffsets(
    ctx: SnappedPositionContext,
    facing: 'front' | 'rotated'
) {
    const {scale, targetValues} = ctx;
    const width = getScaledNum(ctx.width, scale);
    const height = getScaledNum(ctx.height, scale);
    const targetHasDoors = hasDoors(ctx.targetCabinet as CabinetGroup);
    const isFillerOrVermin = ctx.isFillerProduct || ctx.isVerminProduct;

    // Cabinet front face: carcase + door thickness when doors exist.
    // Front facing historically nudges door thickness by FILLER_FRONT_NUDGE.
    const cabinetFrontFaceOffset = targetHasDoors
        ? facing === 'front'
            ? ctx.doorThickness - FILLER_FRONT_NUDGE
            : ctx.doorThickness
        : 0;

    // Filler/vermin origin sits at the back of their front piece, so subtract
    // fillerFrontDepth to keep their front flush with that face.
    const fillerFrontAlignOffset = isFillerOrVermin
        ? cabinetFrontFaceOffset -
          ctx.fillerFrontDepth -
          (ctx.isFillerProduct ? FILLER_FRONT_NUDGE : 0)
        : 0;

    // Front uses live doorThickness for panels; rotated facings keep +4.
    const panelProductOffset =
        ctx.isPanelProduct && targetHasDoors
            ? facing === 'front'
                ? ctx.doorThickness
                : ROTATED_PANEL_DOOR_OFFSET
            : 0;

    const hasDoorsOffset =
        ctx.isUpperProduct && ctx.isFillerProduct && targetHasDoors
            ? 0
            : ctx.isPanelProduct
              ? panelProductOffset
              : fillerFrontAlignOffset;

    return {
        width,
        height,
        targetValuesDepth: getScaledNum(targetValues.cabinet_depth, scale),
        verminWidthOffset: ctx.isVerminProduct ? width : 0,
        panelFillerOffset:
            ctx.isPanelProduct || ctx.isFillerProduct ? width : 0,
        hasDoorsOffset,
        fillerFrontAlignOffset,
        lengthDepth: getScaledLShapedRightDepth(targetValues, scale),
        widthDepth: getScaledLShapedLeftDepth(targetValues, scale),
    };
}

/**
 * Position (x, z) for front-facing cabinet and given side.
 * @param {SnappedPositionContext} ctx - Snapped position context
 * @param {'left' | 'right'} side - Placement side
 * @return {{x: number, z: number}} x and z coordinates
 */
function positionForFront(
    ctx: SnappedPositionContext,
    side: 'left' | 'right'
): {x: number; z: number} {
    const {targetBox} = ctx;
    const {
        targetValuesDepth,
        verminWidthOffset,
        panelFillerOffset,
        hasDoorsOffset,
        fillerFrontAlignOffset,
        lengthDepth,
        widthDepth,
    } = getAccessoryPlacementOffsets(ctx, 'front');

    if (ctx.isTargetCornerOrLShaped) {
        if (side === 'right') {
            return {
                x: targetBox.max.x + panelFillerOffset,
                z: targetBox.min.z + lengthDepth + fillerFrontAlignOffset,
            };
        }
        return {
            x: targetBox.min.x + widthDepth + hasDoorsOffset,
            z: targetBox.max.z + verminWidthOffset,
        };
    }

    const z = targetBox.min.z + targetValuesDepth + hasDoorsOffset;
    return side === 'right'
        ? {x: targetBox.max.x + panelFillerOffset, z}
        : {x: targetBox.min.x - verminWidthOffset, z};
}

/**
 * Position (x, z) for right-facing cabinet and given side.
 * Depth is measured from max (`max - depth`), so front offset is subtracted.
 * @param {SnappedPositionContext} ctx - Snapped position context
 * @param {'left' | 'right'} side - Placement side
 * @return {{x: number, z: number}} x and z coordinates
 */
function positionForRight(
    ctx: SnappedPositionContext,
    side: 'left' | 'right'
): {x: number; z: number} {
    const {targetBox} = ctx;
    const {
        targetValuesDepth,
        verminWidthOffset,
        panelFillerOffset,
        hasDoorsOffset,
        lengthDepth,
        widthDepth,
    } = getAccessoryPlacementOffsets(ctx, 'rotated');

    if (ctx.isTargetCornerOrLShaped) {
        if (side === 'right') {
            return {
                x: targetBox.max.x - lengthDepth - hasDoorsOffset,
                z: targetBox.max.z + panelFillerOffset,
            };
        }
        return {
            x: targetBox.min.x - verminWidthOffset,
            z: targetBox.min.z + widthDepth + hasDoorsOffset,
        };
    }

    const x = targetBox.max.x - targetValuesDepth - hasDoorsOffset;
    return side === 'right'
        ? {x, z: targetBox.max.z + panelFillerOffset}
        : {x, z: targetBox.min.z - verminWidthOffset};
}

/**
 * Position (x, z) for back-facing cabinet and given side.
 * Depth is measured from max, so front offset is subtracted.
 * @param {SnappedPositionContext} ctx - Snapped position context
 * @param {'left' | 'right'} side - Placement side
 * @return {{x: number, z: number}} x and z coordinates
 */
function positionForBack(
    ctx: SnappedPositionContext,
    side: 'left' | 'right'
): {x: number; z: number} {
    const {targetBox} = ctx;
    const {
        targetValuesDepth,
        verminWidthOffset,
        panelFillerOffset,
        hasDoorsOffset,
        lengthDepth,
        widthDepth,
    } = getAccessoryPlacementOffsets(ctx, 'rotated');

    if (ctx.isTargetCornerOrLShaped) {
        if (side === 'right') {
            return {
                x: targetBox.max.x - widthDepth - hasDoorsOffset,
                z: targetBox.min.z - verminWidthOffset,
            };
        }
        return {
            x: targetBox.min.x - panelFillerOffset,
            z: targetBox.max.z - lengthDepth - hasDoorsOffset,
        };
    }

    const z = targetBox.max.z - targetValuesDepth - hasDoorsOffset;
    return side === 'right'
        ? {x: targetBox.max.x + verminWidthOffset, z}
        : {x: targetBox.min.x - panelFillerOffset, z};
}

/**
 * Position (x, z) for left-facing cabinet and given side.
 * Depth is measured along +X from min (`min + depth + offset`).
 * @param {SnappedPositionContext} ctx - Snapped position context
 * @param {'left' | 'right'} side - Placement side
 * @return {{x: number, z: number}} x and z coordinates
 */
function positionForLeft(
    ctx: SnappedPositionContext,
    side: 'left' | 'right'
): {x: number; z: number} {
    const {targetBox} = ctx;
    const {
        width,
        height,
        targetValuesDepth,
        verminWidthOffset,
        panelFillerOffset,
        hasDoorsOffset,
        lengthDepth,
        widthDepth,
    } = getAccessoryPlacementOffsets(ctx, 'rotated');

    if (ctx.isTargetCornerOrLShaped) {
        if (side === 'right') {
            // Right wing depth is max-based, so subtract hasDoorsOffset.
            return {
                x: targetBox.max.x + verminWidthOffset,
                z: targetBox.max.z - widthDepth - hasDoorsOffset,
            };
        }

        const lshapedZOffset = ctx.isPanelProduct
            ? width
            : ctx.isFillerProduct
              ? height
              : 0;

        return {
            x: targetBox.min.x + lengthDepth + hasDoorsOffset,
            z: targetBox.min.z - lshapedZOffset,
        };
    }

    const x = targetBox.min.x + targetValuesDepth + hasDoorsOffset;
    return side === 'right'
        ? {x, z: targetBox.max.z + verminWidthOffset}
        : {x, z: targetBox.min.z - panelFillerOffset};
}

/**
 * Returns (x, z) for the given cabinet direction and side.
 * @param {SnappedPositionContext} ctx - Snapped position context
 * @param {CabinetDirection} direction - Cabinet facing direction
 * @param {'left' | 'right'} side - Placement side
 * @return {{x: number, z: number}} x and z coordinates
 */
function getPositionByDirection(
    ctx: SnappedPositionContext,
    direction: CabinetDirection,
    side: 'left' | 'right'
): {x: number; z: number} {
    switch (normalizeCabinetDirectionForPlacement(direction)) {
        case 'front':
            return positionForFront(ctx, side);
        case 'right':
            return positionForRight(ctx, side);
        case 'back':
            return positionForBack(ctx, side);
        case 'left':
        default:
            return positionForLeft(ctx, side);
    }
}

/**
 * Calculates the position for a new cabinet next to a target cabinet.
 * Uses the same logic as cabinetSpawn.ts for consistent positioning.
 * Adapts to cabinet rotation for proper panel/filler placement.
 *
 * @param {CabinetMesh} targetCabinet - The existing cabinet to position next to
 * @param {CabinetMesh} newCabinet - The new cabinet being positioned
 * @param {CabinetMesh[]} allCabinets - All existing cabinets (unused but kept for compatibility)
 * @param {'left' | 'right'} side - Which side of the target to position on
 * @return {THREE.Vector3} The calculated position for the new cabinet
 */
export const calculateSnappedPosition = (
    targetCabinet: CabinetMesh,
    newCabinet: CabinetMesh,
    allCabinets: CabinetMesh[],
    side: 'left' | 'right'
): THREE.Vector3 => {
    const scale = getScale();
    const targetBox =
        computeWorldBoundsExcludingAnimatedDoorParts(targetCabinet);
    const {width, height, depth} = getGroupWidth(newCabinet);
    const isPanelProduct = Boolean(newCabinet.userData.isPanelProduct);
    const isVerminProduct = Boolean(newCabinet.userData.isVerminProduct);
    const isFillerProduct = Boolean(newCabinet.userData.isFillerProduct);
    const isUpperProduct = Boolean(newCabinet.userData.isUpperProduct);
    const isTargetCornerOrLShaped = Boolean(
        targetCabinet.userData.isLShapedProduct
    );
    const cabinetDirection = normalizeCabinetDirectionForPlacement(
        targetCabinet.userData.currentDirection
    );
    const targetValues = (targetCabinet.userData.values ??
        {}) as TargetCabinetValues;

    const doorThickness =
        (Number(targetValues.cabinet_ext_thickness) + 3.5 || 0) * scale;
    const fillerFrontDepth = getScaledNum(
        newCabinet.userData.values?.cabinet_ext_thickness,
        scale
    );

    const isAccessory = isPanelProduct || isVerminProduct || isFillerProduct;
    if (isAccessory) {
        applyAccessoryRotation(
            targetCabinet,
            newCabinet,
            side,
            isTargetCornerOrLShaped,
            cabinetDirection
        );
    }

    const positionY = targetCabinet.position.y;
    const ctx: SnappedPositionContext = {
        targetBox,
        targetValues,
        scale,
        positionY,
        positionX: targetCabinet.position.x,
        positionZ: targetCabinet.position.z,
        width,
        height,
        doorThickness,
        fillerFrontDepth,
        isPanelProduct,
        isVerminProduct,
        isFillerProduct,
        isTargetCornerOrLShaped,
        targetCabinet,
        depth,
        isUpperProduct,
    };

    const {x, z} = getPositionByDirection(ctx, cabinetDirection, side);
    return new THREE.Vector3(x, positionY, z);
};

/**
 * Checks if any existing cabinet has "add on left/right" positioning flags
 * and calculates the snapped position for a new cabinet next to it
 *
 * @param {CabinetMesh[]} existingCabinets - Array of existing cabinet meshes in the scene
 * @param {CabinetMesh} newCabinet - The new cabinet being positioned
 * @param {THREE.Vector3} defaultPosition - Default position to use if no target cabinet is found
 * @return {object} Object containing the calculated position, target cabinet, and positioning flag
 */
export const getTargetPositionNextToCabinet = (
    existingCabinets: CabinetMesh[],
    newCabinet: CabinetMesh,
    defaultPosition: THREE.Vector3
): {
    position: THREE.Vector3;
    targetCabinet: CabinetMesh | undefined;
    positionedNextToTarget: boolean;
} => {
    // Find cabinet with positioning flag
    const targetCabinet = findCabinetWithPositioningFlag(existingCabinets);

    if (!targetCabinet) {
        return {
            position: defaultPosition,
            targetCabinet: undefined,
            positionedNextToTarget: false,
        };
    }

    const side = getSideFromCabinet(targetCabinet);
    // Use the existing snapping system to calculate the precise snapped position
    const newPosition = calculateSnappedPosition(
        targetCabinet,
        newCabinet,
        existingCabinets,
        side
    );

    return {
        position: newPosition,
        targetCabinet,
        positionedNextToTarget: true,
    };
};

/**
 * Clears the positioning flags on the target cabinet after positioning is complete
 * Only clears if the new cabinet is fully loaded (not a temp mesh and has a real ID)
 *
 * @param {CabinetMesh | undefined} targetCabinet - The target cabinet that had the positioning flags
 * @param {boolean} tempMesh - Whether the new cabinet is a temporary mesh
 * @param {string | number} productJobCabinetId - The job cabinet ID of the new cabinet being positioned
 */
export const clearTargetPositioningFlagsIfReady = (
    targetCabinet: CabinetMesh | undefined,
    tempMesh: boolean,
    productJobCabinetId: string | number
): void => {
    if (!targetCabinet) return;

    const hasRealId = !isNaN(Number(productJobCabinetId));
    const isFullyLoaded = !tempMesh && hasRealId;

    if (isFullyLoaded) {
        // Cabinet is fully loaded with real ID, safe to clear flags now
        clearCabinetPositioningFlags(targetCabinet);
    }
};

/**
 * Checks if a cabinet should use side button positioning
 * @param {CabinetMesh[]} existingCabinets - Array of existing cabinet meshes
 * @return {boolean} True if any cabinet has positioning flags set
 */
export const shouldUseSideButtonPositioning = (
    existingCabinets: CabinetMesh[]
): boolean => {
    return findCabinetWithPositioningFlag(existingCabinets) !== undefined;
};

const ADJACENCY_TOLERANCE = 3.3;
const SIDE_BUTTON_Y_OVERLAP_TOLERANCE = 0.01;

/**
 * Returns true when two cabinets share usable vertical space.
 * Touching exactly at the top/bottom edge is not enough to block a side button.
 * @param {THREE.Box3} a - First bounding box
 * @param {THREE.Box3} b - Second bounding box
 * @param {number} tolerance - Minimum overlap required
 * @return {boolean} true when the boxes overlap in Y by more than the tolerance
 */
function boxesShareUsableYBand(
    a: THREE.Box3,
    b: THREE.Box3,
    tolerance: number = SIDE_BUTTON_Y_OVERLAP_TOLERANCE
): boolean {
    const yOverlap = Math.min(a.max.y, b.max.y) - Math.max(a.min.y, b.min.y);
    return yOverlap > tolerance;
}

/**
 * Returns true if the given cabinet box is adjacent to the target on the given side.
 * Exported for side-attachment detection in `sideAttachmentReposition`.
 * @param {THREE.Box3} targetBox - The target cabinet box
 * @param {THREE.Box3} cabinetBox - The cabinet box to check adjacency for
 * @param {string} cabinetDirection - The direction the cabinet is facing
 * @param {boolean} isLShaped - Whether the cabinet is L-shaped
 * @param {'left' | 'right'} side - Which side to check
 * @param {number} [tolerance] - The tolerance for adjacency detection
 * @return {boolean} True if the cabinet box is adjacent to the target on the given side
 */
export function isCabinetAdjacentOnSide(
    targetBox: THREE.Box3,
    cabinetBox: THREE.Box3,
    cabinetDirection: string,
    isLShaped: boolean,
    side: 'left' | 'right',
    tolerance: number = ADJACENCY_TOLERANCE
): boolean {
    if (!boxesShareUsableYBand(targetBox, cabinetBox)) return false;

    if (isLShaped && cabinetDirection === 'right' && side === 'left') {
        const wingX = targetBox.min.x;
        const zOverlap =
            cabinetBox.min.z <= targetBox.max.z &&
            cabinetBox.max.z >= targetBox.min.z;
        if (!zOverlap) return false;
        const xDistance = wingX - cabinetBox.max.x;
        return xDistance >= -tolerance && xDistance <= tolerance * 4;
    }
    if (isLShaped && cabinetDirection === 'left' && side === 'right') {
        const wingX = targetBox.max.x;
        const zOverlap =
            cabinetBox.min.z <= targetBox.max.z &&
            cabinetBox.max.z >= targetBox.min.z;
        if (!zOverlap) return false;
        const xDistance = cabinetBox.min.x - wingX;
        return xDistance >= -tolerance && xDistance <= tolerance * 4;
    }
    if (isLShaped && cabinetDirection === 'back' && side === 'right') {
        const backZ = targetBox.min.z;
        const xOverlap =
            cabinetBox.min.x <= targetBox.max.x &&
            cabinetBox.max.x >= targetBox.min.x;
        if (!xOverlap) return false;
        const zDistance = backZ - cabinetBox.max.z;
        return zDistance >= -tolerance && zDistance <= tolerance * 4;
    }
    if (
        isLShaped &&
        side === 'left' &&
        (cabinetDirection === 'front' || cabinetDirection === 'left')
    ) {
        const frontZ = targetBox.max.z;
        const zDistance = cabinetBox.min.z - frontZ;
        const xOverlap =
            cabinetBox.min.x <= targetBox.max.x &&
            cabinetBox.max.x >= targetBox.min.x;

        return (
            zDistance >= -tolerance && zDistance <= tolerance * 4 && xOverlap
        );
    }

    // Standard logic
    if (cabinetDirection === 'front' || cabinetDirection === 'back') {
        const targetBoundary =
            side === 'right' ? targetBox.max.x : targetBox.min.x;
        const xDistance =
            side === 'right'
                ? cabinetBox.min.x - targetBoundary
                : targetBoundary - cabinetBox.max.x;
        const zOverlap =
            cabinetBox.min.z <= targetBox.max.z &&
            cabinetBox.max.z >= targetBox.min.z;
        return (
            xDistance >= -tolerance && xDistance <= tolerance * 4 && zOverlap
        );
    }
    const targetBoundary = side === 'right' ? targetBox.max.z : targetBox.min.z;
    const zDistance =
        side === 'right'
            ? cabinetBox.min.z - targetBoundary
            : targetBoundary - cabinetBox.max.z;
    const xOverlap =
        cabinetBox.min.x <= targetBox.max.x &&
        cabinetBox.max.x >= targetBox.min.x;
    return zDistance >= -tolerance && zDistance <= tolerance * 4 && xOverlap;
}

/**
 * Checks if there's already a cabinet or panel on the specified side of the target cabinet
 * @param {CabinetMesh} targetCabinet - The cabinet to check sides for
 * @param {'left' | 'right'} side - Which side to check
 * @param {CabinetMesh[]} allCabinets - All existing cabinets in the scene
 * @return {boolean} True if the side is occupied (has a cabinet/panel)
 */
export const isSideOccupied = (
    targetCabinet: CabinetMesh,
    side: 'left' | 'right',
    allCabinets: CabinetMesh[]
): boolean => {
    const targetBox =
        computeWorldBoundsExcludingAnimatedDoorParts(targetCabinet);
    const cabinetDirection = targetCabinet.userData.currentDirection || 'front';
    const isLShaped = Boolean(targetCabinet.userData.isLShapedProduct);

    for (const cabinet of allCabinets) {
        if (cabinet.userData.id === targetCabinet.userData.id) continue;
        if (cabinet.userData.isTempMesh) continue;

        const cabinetBox =
            computeWorldBoundsExcludingAnimatedDoorParts(cabinet);
        if (
            isCabinetAdjacentOnSide(
                targetBox,
                cabinetBox,
                cabinetDirection,
                isLShaped,
                side
            )
        ) {
            return true;
        }
    }
    return false;
};

export const getSidesOccupied = (
    targetCabinet: CabinetMesh,
    allCabinets: CabinetMesh[]
): {left: boolean; right: boolean} => {
    const targetBox =
        computeWorldBoundsExcludingAnimatedDoorParts(targetCabinet);
    const cabinetDirection = targetCabinet.userData.currentDirection || 'front';
    const isLShaped = Boolean(targetCabinet.userData.isLShapedProduct);

    let left = false;
    let right = false;

    for (const cabinet of allCabinets) {
        if (cabinet.userData.id === targetCabinet.userData.id) continue;
        if (cabinet.userData.isTempMesh) continue;

        const cabinetBox =
            computeWorldBoundsExcludingAnimatedDoorParts(cabinet);
        if (
            isCabinetAdjacentOnSide(
                targetBox,
                cabinetBox,
                cabinetDirection,
                isLShaped,
                'left'
            )
        ) {
            left = true;
        }
        if (
            isCabinetAdjacentOnSide(
                targetBox,
                cabinetBox,
                cabinetDirection,
                isLShaped,
                'right'
            )
        ) {
            right = true;
        }
        if (left && right) break;
    }
    return {left, right};
};

type CabinetBoundsEntry = {
    box: THREE.Box3;
    id: unknown;
    isTempMesh: boolean;
};

type SideButtonAnalysis = {
    leftDistance: number | null;
    rightDistance: number | null;
    leftOccupied: boolean;
    rightOccupied: boolean;
    leftHasOverlap: boolean;
    rightHasOverlap: boolean;
};

/**
 * Determines whether an overlapping cabinet blocks the rendered left/right side buttons.
 * @param {THREE.Box3} targetBox - Selected cabinet bounds
 * @param {THREE.Box3} otherBox - Other cabinet bounds
 * @param {string} cabinetDirection - Selected cabinet direction
 * @param {boolean} isLShaped - Whether the selected cabinet is L-shaped
 * @param {number} cabinetWidthDepth - L-shape wing depth in scaled units
 * @return {{left: boolean, right: boolean}} Overlap flags for each side button
 */
function getCabinetOverlapFlags(
    targetBox: THREE.Box3,
    otherBox: THREE.Box3,
    cabinetDirection: string,
    isLShaped: boolean,
    cabinetWidthDepth: number
): {left: boolean; right: boolean} {
    let left = false;
    let right = false;

    if (
        !boxesShareUsableYBand(targetBox, otherBox) ||
        !targetBox.intersectsBox(otherBox)
    ) {
        return {left, right};
    }

    if (cabinetDirection === 'front') {
        if (isLShaped) {
            const frontZ = targetBox.max.z;
            const wingXMin = targetBox.min.x;
            const wingXMax = targetBox.min.x + cabinetWidthDepth;
            const overlapsWingFrontFace =
                otherBox.max.z >= frontZ - ADJACENCY_TOLERANCE &&
                otherBox.min.z <= frontZ + ADJACENCY_TOLERANCE &&
                otherBox.max.x > wingXMin &&
                otherBox.min.x < wingXMax;

            left = overlapsWingFrontFace;
            right = otherBox.max.x > targetBox.max.x;
        } else {
            left = otherBox.min.x < targetBox.min.x;
            right = otherBox.max.x > targetBox.max.x;
        }
        return {left, right};
    }

    if (cabinetDirection === 'back') {
        if (isLShaped) {
            left = otherBox.min.x < targetBox.min.x;
            right = otherBox.min.z < targetBox.min.z;
        } else {
            left = otherBox.min.x < targetBox.min.x;
            right = otherBox.max.x > targetBox.max.x;
        }
        return {left, right};
    }

    if (cabinetDirection === 'right' && isLShaped) {
        left = otherBox.min.x < targetBox.min.x;
        right = otherBox.max.z > targetBox.max.z;
        return {left, right};
    }

    if (cabinetDirection === 'left' && isLShaped) {
        left = otherBox.min.z < targetBox.min.z;
        right = otherBox.max.x > targetBox.max.x;
        return {left, right};
    }

    left = otherBox.min.z < targetBox.min.z;
    right = otherBox.max.z > targetBox.max.z;
    return {left, right};
}

/**
 * Reuses cached cabinet bounds to determine whether another cabinet sits between the
 * target cabinet and the nearest cabinet found on the requested side.
 * @param {THREE.Box3} targetBox - Selected cabinet bounds
 * @param {THREE.Box3} nearestBox - Bounds of the nearest side cabinet
 * @param {CabinetBoundsEntry[]} cabinetEntries - Cached bounds for other cabinets
 * @param {unknown} targetId - Selected cabinet identifier
 * @param {unknown} nearestId - Nearest cabinet identifier
 * @param {'left' | 'right'} side - Side being evaluated
 * @param {string} cabinetDirection - Selected cabinet direction
 * @param {boolean} isLShaped - Whether the selected cabinet is L-shaped
 * @return {boolean} True when another cabinet blocks the nearest candidate
 */
function hasCabinetBetweenTargetAndNearestCached(
    targetBox: THREE.Box3,
    nearestBox: THREE.Box3,
    cabinetEntries: CabinetBoundsEntry[],
    targetId: unknown,
    nearestId: unknown,
    side: 'left' | 'right',
    cabinetDirection: string,
    isLShaped: boolean
): boolean {
    const tolerance = 5;
    const isLShapedLeftFront =
        (cabinetDirection === 'front' || cabinetDirection === 'back') &&
        isLShaped &&
        side === 'left';
    const isLShapedRightLeft =
        cabinetDirection === 'right' && isLShaped && side === 'left';
    const isLShapedLeftRight =
        cabinetDirection === 'left' && isLShaped && side === 'right';
    const isLShapedBackRight =
        cabinetDirection === 'back' && isLShaped && side === 'right';

    for (const entry of cabinetEntries) {
        if (entry.isTempMesh) continue;
        if (entry.id === targetId || entry.id === nearestId) continue;

        const cabinetBox = entry.box;
        if (!boxesShareUsableYBand(targetBox, cabinetBox)) continue;

        let isBetween = false;

        if (isLShapedLeftFront) {
            const hasXOverlap =
                cabinetBox.min.x <= targetBox.max.x + tolerance &&
                cabinetBox.max.x >= targetBox.min.x - tolerance;
            if (cabinetDirection === 'front') {
                isBetween =
                    hasXOverlap &&
                    cabinetBox.max.z >= targetBox.max.z - tolerance &&
                    cabinetBox.min.z <= nearestBox.min.z + tolerance;
            } else {
                isBetween =
                    hasXOverlap &&
                    cabinetBox.min.z <= targetBox.min.z + tolerance &&
                    cabinetBox.max.z >= nearestBox.max.z - tolerance;
            }
        } else if (isLShapedRightLeft) {
            const hasZOverlap =
                cabinetBox.min.z <= targetBox.max.z + tolerance &&
                cabinetBox.max.z >= targetBox.min.z - tolerance;
            isBetween =
                hasZOverlap &&
                cabinetBox.max.x <= targetBox.min.x + tolerance &&
                cabinetBox.min.x >= nearestBox.max.x - tolerance;
        } else if (isLShapedLeftRight) {
            const hasZOverlap =
                cabinetBox.min.z <= targetBox.max.z + tolerance &&
                cabinetBox.max.z >= targetBox.min.z - tolerance;
            isBetween =
                hasZOverlap &&
                cabinetBox.min.x >= targetBox.max.x - tolerance &&
                cabinetBox.max.x <= nearestBox.min.x + tolerance;
        } else if (isLShapedBackRight) {
            const hasXOverlap =
                cabinetBox.min.x <= targetBox.max.x + tolerance &&
                cabinetBox.max.x >= targetBox.min.x - tolerance;
            isBetween =
                hasXOverlap &&
                cabinetBox.max.z <= targetBox.min.z + tolerance &&
                cabinetBox.min.z >= nearestBox.max.z - tolerance;
        } else if (
            cabinetDirection === 'front' ||
            cabinetDirection === 'back'
        ) {
            const hasZOverlap =
                cabinetBox.min.z <= targetBox.max.z + tolerance &&
                cabinetBox.max.z >= targetBox.min.z - tolerance;
            if (hasZOverlap) {
                if (side === 'right') {
                    isBetween =
                        cabinetBox.min.x >= targetBox.max.x - tolerance &&
                        cabinetBox.max.x <= nearestBox.min.x + tolerance;
                } else {
                    isBetween =
                        cabinetBox.max.x <= targetBox.min.x + tolerance &&
                        cabinetBox.min.x >= nearestBox.max.x - tolerance;
                }
            }
        } else {
            const hasXOverlap =
                cabinetBox.min.x <= targetBox.max.x + tolerance &&
                cabinetBox.max.x >= targetBox.min.x - tolerance;
            if (hasXOverlap) {
                if (side === 'right') {
                    isBetween =
                        cabinetBox.min.z >= targetBox.max.z - tolerance &&
                        cabinetBox.max.z <= nearestBox.min.z + tolerance;
                } else {
                    isBetween =
                        cabinetBox.max.z <= targetBox.min.z + tolerance &&
                        cabinetBox.min.z >= nearestBox.max.z - tolerance;
                }
            }
        }

        if (isBetween) return true;
    }

    return false;
}

/**
 * Performs the side-button visibility geometry checks in one cached pass.
 * @param {CabinetMesh} targetCabinet - Selected cabinet
 * @param {THREE.Box3} targetBox - Cached selected cabinet bounds
 * @param {CabinetMesh[]} allCabinets - Cabinets to compare against
 * @param {string} cabinetDirection - Selected cabinet direction
 * @param {boolean} isLShaped - Whether the selected cabinet is L-shaped
 * @param {number} cabinetWidthDepth - L-shape wing depth in scaled units
 * @return {SideButtonAnalysis} Distances plus occupancy/overlap state for both sides
 */
function analyzeSideButtons(
    targetCabinet: CabinetMesh,
    targetBox: THREE.Box3,
    allCabinets: CabinetMesh[],
    cabinetDirection: string,
    isLShaped: boolean,
    cabinetWidthDepth: number
): SideButtonAnalysis {
    const cabinetEntries: CabinetBoundsEntry[] = [];
    const targetId = targetCabinet.userData.id;

    let leftOccupied = false;
    let rightOccupied = false;
    let leftHasOverlap = false;
    let rightHasOverlap = false;
    let leftNearest: CabinetBoundsEntry | null = null;
    let leftMinDistance = Infinity;
    let rightNearest: CabinetBoundsEntry | null = null;
    let rightMinDistance = Infinity;

    for (const cabinet of allCabinets) {
        if (cabinet.userData.id === targetId) continue;

        const entry: CabinetBoundsEntry = {
            box: computeWorldBoundsExcludingAnimatedDoorParts(cabinet),
            id: cabinet.userData.id,
            isTempMesh: Boolean(cabinet.userData.isTempMesh),
        };
        cabinetEntries.push(entry);

        const overlapFlags = getCabinetOverlapFlags(
            targetBox,
            entry.box,
            cabinetDirection,
            isLShaped,
            cabinetWidthDepth
        );
        leftHasOverlap = leftHasOverlap || overlapFlags.left;
        rightHasOverlap = rightHasOverlap || overlapFlags.right;

        if (entry.isTempMesh) continue;

        if (
            !leftOccupied &&
            isCabinetAdjacentOnSide(
                targetBox,
                entry.box,
                cabinetDirection,
                isLShaped,
                'left'
            )
        ) {
            leftOccupied = true;
        }

        if (
            !rightOccupied &&
            isCabinetAdjacentOnSide(
                targetBox,
                entry.box,
                cabinetDirection,
                isLShaped,
                'right'
            )
        ) {
            rightOccupied = true;
        }

        const leftResult = getDistanceAndSideForCabinet(
            entry.box,
            targetBox,
            cabinetDirection,
            isLShaped,
            'left'
        );
        if (
            leftResult &&
            leftResult.distance > 0 &&
            leftResult.distance < leftMinDistance
        ) {
            leftMinDistance = leftResult.distance;
            leftNearest = entry;
        }

        const rightResult = getDistanceAndSideForCabinet(
            entry.box,
            targetBox,
            cabinetDirection,
            isLShaped,
            'right'
        );
        if (
            rightResult &&
            rightResult.isOnCorrectSide &&
            rightResult.distance > 0 &&
            rightResult.distance < rightMinDistance
        ) {
            rightMinDistance = rightResult.distance;
            rightNearest = entry;
        }
    }

    if (
        leftNearest &&
        hasCabinetBetweenTargetAndNearestCached(
            targetBox,
            leftNearest.box,
            cabinetEntries,
            targetId,
            leftNearest.id,
            'left',
            cabinetDirection,
            isLShaped
        )
    ) {
        leftNearest = null;
    }

    if (
        rightNearest &&
        hasCabinetBetweenTargetAndNearestCached(
            targetBox,
            rightNearest.box,
            cabinetEntries,
            targetId,
            rightNearest.id,
            'right',
            cabinetDirection,
            isLShaped
        )
    ) {
        rightNearest = null;
    }

    return {
        leftDistance: leftNearest ? leftMinDistance : null,
        rightDistance: rightNearest ? rightMinDistance : null,
        leftOccupied,
        rightOccupied,
        leftHasOverlap,
        rightHasOverlap,
    };
}

/**
 * Creates a dummy cabinet for space checking and calculates the snapped position
 * This handles the snapping logic separately from clamping
 * @param {CabinetMesh} targetCabinet - The cabinet to check space for
 * @param {'left' | 'right'} side - Which side to check
 * @param {CabinetMesh[]} allCabinets - All existing cabinets in the scene
 * @return {{dummyCabinet: CabinetMesh, simulatedPosition: THREE.Vector3, isUpperProduct: boolean}} The dummy cabinet, simulated position, and upper product flag
 */
const calculateSnappedPositionForSide = (
    targetCabinet: CabinetMesh,
    side: 'left' | 'right',
    allCabinets: CabinetMesh[]
): {
    dummyCabinet: CabinetMesh;
    simulatedPosition: THREE.Vector3;
    isUpperProduct: boolean;
} => {
    // Create a temporary dummy cabinet to simulate placement
    const dummyCabinet = targetCabinet.clone();
    dummyCabinet.userData.id = 'dummy-check-space';

    // Preserve critical userData properties needed for clamping
    // This ensures upper product detection works correctly
    const isUpperProduct = targetCabinet.userData.isUpperProduct || false;
    dummyCabinet.userData.isUpperProduct = isUpperProduct;
    // Copy currentWall which is needed for upperProductClamping
    if (targetCabinet.userData.currentWall) {
        dummyCabinet.userData.currentWall = targetCabinet.userData.currentWall;
    }

    // Calculate where the cabinet would be placed (snapping logic)
    const simulatedPosition = calculateSnappedPosition(
        targetCabinet,
        dummyCabinet,
        allCabinets,
        side
    );

    return {
        dummyCabinet,
        simulatedPosition,
        isUpperProduct,
    };
};

type ClampingBounds = {
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
};

type DistanceToWallResult = {
    distanceToWall: number;
    wallBoundary: number;
    cabinetEdge: number;
};

/**
 * L-shaped: distance along the axis where the add-button is (wing face).
 * Returns null if (direction, side) is not one of the four L-shaped button cases.
 * @param {string} direction - The direction the cabinet is facing
 * @param {'left' | 'right'} side - Which side to check
 * @param {THREE.Box3} box - The cabinet box
 * @param {ClampingBounds} bounds - The clamping bounds
 * @param {number} dummyDepth - The depth of the dummy cabinet
 * @return {DistanceToWallResult | null} The distance to wall, wall boundary, and cabinet edge
 */
function getLShapedDistanceToWall(
    direction: string,
    side: 'left' | 'right',
    box: THREE.Box3,
    bounds: ClampingBounds,
    dummyDepth: number
): DistanceToWallResult | null {
    if (direction === 'front' && side === 'left') {
        const cabinetEdge = box.max.z;
        const wallBoundary = bounds.maxZ;
        return {
            cabinetEdge,
            wallBoundary,
            distanceToWall: dummyDepth + (wallBoundary - cabinetEdge),
        };
    }
    if (direction === 'back' && side === 'right') {
        const cabinetEdge = box.min.z;
        const wallBoundary = bounds.minZ;
        return {
            cabinetEdge,
            wallBoundary,
            distanceToWall: cabinetEdge - wallBoundary,
        };
    }
    if (direction === 'right' && side === 'left') {
        const cabinetEdge = box.min.x;
        const wallBoundary = bounds.minX;
        return {
            cabinetEdge,
            wallBoundary,
            distanceToWall: cabinetEdge - wallBoundary,
        };
    }
    if (direction === 'left' && side === 'right') {
        const cabinetEdge = box.max.x;
        const wallBoundary = bounds.maxX;
        return {
            cabinetEdge,
            wallBoundary,
            distanceToWall: wallBoundary - cabinetEdge,
        };
    }
    return null;
}

/**
 * Rectangular cabinet: distance to wall by direction and side (X for front/back, Z for left/right).
 * @param {string} direction - The direction the cabinet is facing
 * @param {'left' | 'right'} side - Which side to check
 * @param {THREE.Box3} box - The cabinet box
 * @param {ClampingBounds} bounds - The clamping bounds
 * @param {number} dummyWidth - The width of the dummy cabinet
 * @param {number} dummyDepth - The depth of the dummy cabinet
 * @return {DistanceToWallResult} The distance to wall, wall boundary, and cabinet edge
 */
function getRectangularDistanceToWall(
    direction: string,
    side: 'left' | 'right',
    box: THREE.Box3,
    bounds: ClampingBounds,
    dummyWidth: number,
    dummyDepth: number
): DistanceToWallResult {
    const isFrontOrNormal = direction === 'front' || direction === 'normal';
    const isRight = direction === 'right';
    const isLeft = direction === 'left';

    if (isFrontOrNormal) {
        if (side === 'right') {
            const cabinetEdge = box.max.x;
            const wallBoundary = bounds.maxX;
            return {
                cabinetEdge,
                wallBoundary,
                distanceToWall: dummyWidth + (wallBoundary - cabinetEdge),
            };
        }
        const cabinetEdge = box.min.x;
        const wallBoundary = bounds.minX;
        return {
            cabinetEdge,
            wallBoundary,
            distanceToWall: cabinetEdge - wallBoundary,
        };
    }

    if (isRight) {
        if (side === 'right') {
            const cabinetEdge = box.max.z;
            const wallBoundary = bounds.maxZ;
            return {
                cabinetEdge,
                wallBoundary,
                distanceToWall: dummyDepth + (wallBoundary - cabinetEdge),
            };
        }
        const cabinetEdge = box.min.z;
        const wallBoundary = bounds.minZ;
        return {
            cabinetEdge,
            wallBoundary,
            distanceToWall: cabinetEdge - wallBoundary,
        };
    }

    if (isLeft) {
        if (side === 'right') {
            const cabinetEdge = box.max.z;
            const wallBoundary = bounds.maxZ;
            return {
                cabinetEdge,
                wallBoundary,
                distanceToWall: wallBoundary - cabinetEdge,
            };
        }
        const cabinetEdge = box.min.z;
        const wallBoundary = bounds.minZ;
        return {
            cabinetEdge,
            wallBoundary,
            distanceToWall: dummyDepth + (cabinetEdge - wallBoundary),
        };
    }

    // back (and any other direction)
    if (side === 'right') {
        const cabinetEdge = box.max.x;
        const wallBoundary = bounds.maxX;
        return {
            cabinetEdge,
            wallBoundary,
            distanceToWall: wallBoundary - cabinetEdge,
        };
    }
    const cabinetEdge = box.min.x;
    const wallBoundary = bounds.minX;
    return {
        cabinetEdge,
        wallBoundary,
        distanceToWall: dummyWidth + (cabinetEdge - wallBoundary),
    };
}

/**
 * Calculates the distance to wall for clamping detection
 * This is separate from snapping distance calculation to avoid conflicts
 * @param {CabinetMesh} targetCabinet - The original cabinet
 * @param {CabinetMesh} dummyCabinet - The dummy cabinet for simulation
 * @param {'left' | 'right'} side - Which side to check
 * @param {object} clampingResult - The result from productClamping
 * @return {{distanceToWall: number, wallBoundary: number, cabinetEdge: number}} Distance to wall, wall boundary position, and cabinet edge position
 */
const calculateClampingDistance = (
    targetCabinet: CabinetMesh,
    dummyCabinet: CabinetMesh,
    side: 'left' | 'right',
    clampingResult: ClampingBounds
): DistanceToWallResult => {
    const box = computeWorldBoundsExcludingAnimatedDoorParts(targetCabinet);
    const {width: dummyWidth, depth: dummyDepth} = getGroupWidth(dummyCabinet);
    const direction = targetCabinet.userData.currentDirection || 'front';
    const isLShaped = Boolean(targetCabinet.userData.isLShapedProduct);

    if (isLShaped) {
        const result = getLShapedDistanceToWall(
            direction,
            side,
            box,
            clampingResult,
            dummyDepth
        );
        if (result !== null) return result;
    }

    return getRectangularDistanceToWall(
        direction,
        side,
        box,
        clampingResult,
        dummyWidth,
        dummyDepth
    );
};

/**
 * Checks clamping boundaries and calculates distance to wall
 * This handles the clamping logic separately from snapping
 * @param {CabinetMesh} targetCabinet - The original cabinet
 * @param {CabinetMesh} dummyCabinet - The dummy cabinet for simulation
 * @param {THREE.Vector3} simulatedPosition - The snapped position
 * @param {boolean} isUpperProduct - Whether this is an upper product
 * @param {'left' | 'right'} side - Which side to check
 * @param {THREE.Mesh} wallGroup - The wall mesh for boundary checking
 * @param {THREE.Camera} camera - The camera (required for productClamping)
 * @param {string} roomType - The room type (required for productClamping)
 * @return {{hasSpace: boolean, distanceToWall: number, positionDiff: number}} Whether there's space, distance to wall, and position difference
 */
const checkClampingBoundaries = (
    targetCabinet: CabinetMesh,
    dummyCabinet: CabinetMesh,
    simulatedPosition: THREE.Vector3,
    isUpperProduct: boolean,
    side: 'left' | 'right',
    wallGroup: THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>,
    camera: THREE.Camera,
    roomType: string
): {
    hasSpace: boolean;
    distanceToWall: number;
    positionDiff: number;
} => {
    // Use productClamping to check if this position would be clamped
    const mouse = new THREE.Vector2(0, 0);

    let clampingResult;
    try {
        clampingResult = productClamping(
            dummyCabinet,
            wallGroup,
            camera,
            mouse,
            simulatedPosition,
            roomType,
            isUpperProduct,
            true // preventAutoRotation
        );
    } catch {
        // If productClamping fails, default to allowing space
        return {
            hasSpace: true,
            distanceToWall: Infinity,
            positionDiff: 0,
        };
    }

    // Calculate distance from simulated position to clamped position
    const positionDiff = simulatedPosition.distanceTo(clampingResult.clamped);

    // Calculate clamping distance separately (this can be modified independently)
    const {distanceToWall: roundedDistanceToWall} = calculateClampingDistance(
        targetCabinet,
        dummyCabinet,
        side,
        clampingResult
    );

    // Check if the cabinet is at the boundary limit
    // NOTE: clampingResult boundaries are for CENTER positions, not edges
    // So we need to compare edges to the actual wall boundaries, not center limits
    const tolerance = WALL_BOUNDARY_TOLERANCE; // Tolerance for boundary detection

    // Apply same 19mm threshold as nearest product check
    // Distance is in scaled units (scale = 0.2), so 19mm = 19 * scale = 3.8 in scaled units
    const scale = getScale();
    const DISTANCE_THRESHOLD_19MM = 20 * scale; // 19mm in scaled units (19 * 0.2 = 3.8)
    const DISTANCE_TOLERANCE = 0.01; // Small tolerance for floating point precision

    // Calculate isAtLimit based on actual distance to wall
    // If the actual distance is very small (within tolerance), we're at the limit
    const isAtLimit = roundedDistanceToWall <= tolerance;

    // Check if there's at least 19mm of space to the wall
    // Use roundedDistanceToWall which represents the space from current cabinet to wall
    // If negative, cabinet is at or past the wall (no space)
    // If less than 19mm, not enough space to add another cabinet
    // This is the primary check - if there's 19mm+ of space, button should show
    if (
        roundedDistanceToWall <= 0 ||
        roundedDistanceToWall <= DISTANCE_THRESHOLD_19MM - DISTANCE_TOLERANCE
    ) {
        return {
            hasSpace: false, // Not enough space at wall (negative or less than 19mm)
            distanceToWall: roundedDistanceToWall,
            positionDiff,
        };
    }

    // If we have 19mm+ of space, the button should show
    // Only check additional conditions if we're close to the limit
    // If at limit (within tolerance), there's no space to add another cabinet
    if (isAtLimit) {
        return {
            hasSpace: false,
            distanceToWall: roundedDistanceToWall,
            positionDiff,
        };
    }

    // Only check position difference if we're VERY close to the wall
    // If the clamped position differs significantly from simulated position AND
    // we're very close to the wall (within 19-30mm), then hide the button
    // This prevents hiding when there's plenty of space (e.g., 80mm, 255mm)
    // Only hide if position was adjusted AND we're within ~30mm of the wall
    const CLOSE_TO_WALL_THRESHOLD = 0; // 30mm in scaled units (30 * scale = 6 when scale is 0.2)
    if (
        positionDiff > MAX_POSITION_DIFF_THRESHOLD &&
        roundedDistanceToWall <= CLOSE_TO_WALL_THRESHOLD
    ) {
        return {
            hasSpace: true, // Space is available (>= 19mm)
            distanceToWall: roundedDistanceToWall,
            positionDiff,
        };
    }

    // Default: space is available
    return {
        hasSpace: true,
        distanceToWall: roundedDistanceToWall,
        positionDiff,
    };
};

/**
 * Gets the clamping distance to wall for a specific side
 * This is used for filtering products based on available space at wall boundaries
 * @param {CabinetMesh} targetCabinet - The cabinet to check distance for
 * @param {'left' | 'right'} side - Which side to check
 * @param {CabinetMesh[]} allCabinets - All existing cabinets in the scene
 * @param {THREE.Mesh} wallGroup - The wall mesh for boundary checking
 * @param {THREE.Camera} camera - The camera (required for productClamping)
 * @param {string} roomType - The room type (required for productClamping)
 * @return {number | null} The clamping distance in scaled units, or null if calculation fails
 */
export const getClampingDistanceForSide = (
    targetCabinet: CabinetMesh,
    side: 'left' | 'right',
    allCabinets: CabinetMesh[],
    wallGroup: THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>,
    camera: THREE.Camera,
    roomType: string
): number | null => {
    // Validate inputs
    // Note: wallGroup is actually a Group (productClamping uses wallGroup.children)
    // but the type signature says Mesh for compatibility
    const wallGroupAsObject = wallGroup as unknown as THREE.Object3D;
    if (
        !targetCabinet ||
        !wallGroup ||
        typeof targetCabinet.clone !== 'function' ||
        typeof wallGroupAsObject.updateWorldMatrix !== 'function' ||
        !wallGroupAsObject.children ||
        wallGroupAsObject.children.length === 0
    ) {
        return null;
    }

    try {
        // Step 1: Calculate snapped position (snapping logic)
        const {dummyCabinet, simulatedPosition, isUpperProduct} =
            calculateSnappedPositionForSide(targetCabinet, side, allCabinets);

        // Step 2: Get clamping result
        const mouse = new THREE.Vector2(0, 0);
        const clampingResult = productClamping(
            dummyCabinet,
            wallGroup,
            camera,
            mouse,
            simulatedPosition,
            roomType,
            isUpperProduct,
            true // preventAutoRotation
        );

        // Step 3: Calculate clamping distance
        const {distanceToWall} = calculateClampingDistance(
            targetCabinet,
            dummyCabinet,
            side,
            clampingResult
        );

        return distanceToWall;
    } catch {
        // If calculation fails, return null
        return null;
    }
};

/**
 * Checks if there's space available on a side by checking if the cabinet is at wall boundary
 * Similar to how wall measurements detect empty space - checks if cabinet edge is at wall limit
 * This function orchestrates the snapping and clamping logic
 * @param {CabinetMesh} targetCabinet - The cabinet to check space for
 * @param {'left' | 'right'} side - Which side to check
 * @param {CabinetMesh[]} allCabinets - All existing cabinets in the scene
 * @param {THREE.Mesh} wallGroup - The wall mesh for boundary checking
 * @param {THREE.Camera} camera - The camera (required for productClamping)
 * @param {string} roomType - The room type (required for productClamping)
 * @return {boolean} True if there's space available, false if at wall limit/no space
 */
export const hasSpaceOnSide = (
    targetCabinet: CabinetMesh,
    side: 'left' | 'right',
    allCabinets: CabinetMesh[],
    wallGroup: THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>,
    camera: THREE.Camera,
    roomType: string
): boolean => {
    // Validate inputs
    // Note: wallGroup is actually a Group (productClamping uses wallGroup.children)
    // but the type signature says Mesh for compatibility
    const wallGroupAsObject = wallGroup as unknown as THREE.Object3D;
    if (
        !targetCabinet ||
        !wallGroup ||
        typeof targetCabinet.clone !== 'function' ||
        typeof wallGroupAsObject.updateWorldMatrix !== 'function' ||
        !wallGroupAsObject.children ||
        wallGroupAsObject.children.length === 0
    ) {
        // If inputs are invalid, default to allowing space
        return true;
    }

    // Step 1: Calculate snapped position (snapping logic)
    const {dummyCabinet, simulatedPosition, isUpperProduct} =
        calculateSnappedPositionForSide(targetCabinet, side, allCabinets);

    // Step 2: Check clamping boundaries (clamping logic)
    const {hasSpace} = checkClampingBoundaries(
        targetCabinet,
        dummyCabinet,
        simulatedPosition,
        isUpperProduct,
        side,
        wallGroup,
        camera,
        roomType
    );

    return hasSpace;
};

/**
 * Creates a button sprite for the add cabinet button
 * @param {boolean} isHovered - Whether the button is being hovered
 * @param {number} opacity - Opacity of the button (0-1)
 * @return {THREE.Sprite} The created sprite
 */
export const createButtonSprite = (
    isHovered = false,
    opacity = 1.0
): THREE.Sprite => {
    // Create canvas for the button
    const canvas = document.createElement('canvas');
    const size = 300;
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d');
    if (!context) return new THREE.Sprite();

    // Clear canvas
    context.clearRect(0, 0, size, size);

    // Calculate circle parameters
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size / 2 - 20;

    // Draw shadow
    context.shadowColor = 'rgba(0, 0, 0, 0.3)';
    context.shadowBlur = isHovered ? 20 : 10;
    context.shadowOffsetX = 0;
    context.shadowOffsetY = isHovered ? 6 : 4;

    // Draw circle background
    context.beginPath();
    context.arc(centerX, centerY, radius, 0, Math.PI * 2);
    context.fillStyle = isHovered ? '#0096e6' : '#00a8ff';
    context.fill();

    // Reset shadow for the plus sign
    context.shadowColor = 'transparent';
    context.shadowBlur = 0;
    context.shadowOffsetX = 0;
    context.shadowOffsetY = 0;

    // Draw plus sign
    context.strokeStyle = 'white';
    context.lineWidth = 20;
    context.lineCap = 'round';

    // Vertical line
    context.beginPath();
    context.moveTo(centerX, centerY - radius * 0.5);
    context.lineTo(centerX, centerY + radius * 0.5);
    context.stroke();

    // Horizontal line
    context.beginPath();
    context.moveTo(centerX - radius * 0.5, centerY);
    context.lineTo(centerX + radius * 0.5, centerY);
    context.stroke();

    // Create texture
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    // Create sprite material with adjustable opacity
    const spriteMaterial = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        sizeAttenuation: false,
        opacity: opacity,
    });

    // Create sprite
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.scale.set(26, 26, 1); // Size of button in screen space (25% smaller than original 35)

    // Ensure sprite renders on top but behind UI elements
    sprite.renderOrder = 998;

    // Make sprite raycastable - important for click detection
    // Sprites need to be in the scene and have proper matrix updates
    sprite.matrixAutoUpdate = true;
    sprite.frustumCulled = false; // Don't cull sprites, always check for intersections

    // Make sprite clickable like other sprites in the system
    // This allows it to be detected by the sprite click system
    sprite.userData.clickable = true;
    sprite.userData.isTextSprite = false; // Not a text sprite, but still clickable

    return sprite;
};

/**
 * Creates a line with distance measurement between two points
 * @param {THREE.Vector3} start - Start point of the line
 * @param {THREE.Vector3} end - End point of the line
 * @param {number} distance - Distance value to display
 * @param {string} side - Which side the measurement is on ('left' | 'right' | 'front' | 'back')
 * @return {THREE.Group} Group containing the line and label
 */
export const createDistanceLine = (
    start: THREE.Vector3,
    end: THREE.Vector3,
    distance: number,
    side: 'left' | 'right' | 'front' | 'back'
): THREE.Group => {
    const group = new THREE.Group();

    // Shorten the line by moving endpoints inward (5 % from each end)
    const lineVector = new THREE.Vector3().subVectors(end, start);
    const shortenFactor = 0.05; // Shorten by 5% from each end (5% total)
    const shortenedStart = start
        .clone()
        .add(lineVector.clone().multiplyScalar(shortenFactor));
    const shortenedEnd = end
        .clone()
        .sub(lineVector.clone().multiplyScalar(shortenFactor));

    // Create line with shortened endpoints using tube geometry for better depth visibility
    const lineDirection = new THREE.Vector3().subVectors(
        shortenedEnd,
        shortenedStart
    );
    const lineLength = lineDirection.length();
    const normalizedDirection = lineDirection.clone().normalize();
    const tubeGeometry = new THREE.CylinderGeometry(1.5, 1.5, lineLength, 8, 1);

    // Rotate and position the tube to align with the line
    const lineMidpoint = new THREE.Vector3()
        .addVectors(shortenedStart, shortenedEnd)
        .multiplyScalar(0.5);
    const up = new THREE.Vector3(0, 1, 0);
    const quaternion = new THREE.Quaternion().setFromUnitVectors(
        up,
        normalizedDirection
    );

    const material = new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 1.0, // Full opacity to match label
        depthTest: false, // Match label - always render bright
        depthWrite: false, // Don't write to depth buffer
        toneMapped: false, // Add this - prevent tone mapping from darkening the color
    });
    material.color.setHex(0x000000); // Set color explicitly - black

    const line = new THREE.Mesh(tubeGeometry, material);
    line.position.copy(lineMidpoint);
    line.setRotationFromQuaternion(quaternion);
    line.renderOrder = 999;
    group.add(line);

    // Create arrows at both ends (Rule 1: arrows are always on the line)
    const direction = new THREE.Vector3()
        .subVectors(shortenedEnd, shortenedStart)
        .normalize();
    const reverseDirection = direction.clone().negate();
    const arrowColor = 0x000000; // Black color

    // Arrow configuration - small arrowheads at the ends pointing inward
    const arrowHeadLength = 10; // Slightly larger for better visibility
    const arrowHeadWidth = 10;
    const arrowShaftLength = 5; // Slightly longer shaft

    // Left arrow: positioned at shortened start, pointing toward center
    const leftArrowPos = shortenedStart.clone();
    const leftArrow = new THREE.ArrowHelper(
        reverseDirection, // Point toward end (inward toward center)
        leftArrowPos,
        arrowShaftLength,
        arrowColor,
        arrowHeadLength,
        arrowHeadWidth
    );
    setArrowHighPriority(leftArrow, 900); // Behind label (label is 1000)
    // Ensure arrow cone always renders regardless of view angle
    if (leftArrow.cone) {
        leftArrow.cone.frustumCulled = false;
        leftArrow.cone.renderOrder = 999; // Behind label (label is 1000)
        // Configure material to render on top but respect cabinet occlusion
        if (
            leftArrow.cone.material &&
            !Array.isArray(leftArrow.cone.material)
        ) {
            leftArrow.cone.material.depthTest = true; // Allow cabinets to occlude
            leftArrow.cone.material.depthWrite = false; // Don't write to depth buffer
            leftArrow.cone.material.side = THREE.DoubleSide; // Render both sides
            leftArrow.cone.material.transparent = true;
            leftArrow.cone.material.opacity = 0.9; // Match label canvas opacity
            // Black color
            if (leftArrow.cone.material instanceof THREE.MeshBasicMaterial) {
                leftArrow.cone.material.color.setHex(0x000000);
            }
        }
    }
    if (leftArrow.line) {
        leftArrow.line.frustumCulled = false;
        leftArrow.line.renderOrder = 999; // Behind label (label is 1000)
        // Configure material to render on top but respect cabinet occlusion
        if (
            leftArrow.line.material &&
            !Array.isArray(leftArrow.line.material)
        ) {
            leftArrow.line.material.depthTest = true; // Allow cabinets to occlude
            leftArrow.line.material.depthWrite = false; // Don't write to depth buffer
            leftArrow.line.material.transparent = true;
            leftArrow.line.material.opacity = 0.9; // Match label canvas opacity
            // Black color
            if (leftArrow.line.material instanceof THREE.LineBasicMaterial) {
                leftArrow.line.material.color.setHex(0x000000);
            }
        }
    }
    group.add(leftArrow);

    // Right arrow: positioned at shortened end, pointing toward center
    const rightArrowPos = shortenedEnd.clone();

    const rightArrow = new THREE.ArrowHelper(
        direction, // Point toward start (inward toward center)
        rightArrowPos,
        arrowShaftLength,
        arrowColor,
        arrowHeadLength,
        arrowHeadWidth
    );
    setArrowHighPriority(rightArrow, 900); // Behind label (label is 1000)
    // Ensure arrow cone always renders regardless of view angle
    if (rightArrow.cone) {
        rightArrow.cone.frustumCulled = false;
        rightArrow.cone.renderOrder = 999; // Behind label (label is 1000)
        // Configure material to render on top but respect cabinet occlusion
        if (
            rightArrow.cone.material &&
            !Array.isArray(rightArrow.cone.material)
        ) {
            rightArrow.cone.material.depthTest = true; // Allow cabinets to occlude
            rightArrow.cone.material.depthWrite = false; // Don't write to depth buffer
            rightArrow.cone.material.side = THREE.DoubleSide; // Render both sides
            rightArrow.cone.material.transparent = true;
            rightArrow.cone.material.opacity = 0.9; // Match label canvas opacity
            // Black color
            if (rightArrow.cone.material instanceof THREE.MeshBasicMaterial) {
                rightArrow.cone.material.color.setHex(0x000000);
            }
        }
    }
    if (rightArrow.line) {
        rightArrow.line.frustumCulled = false;
        rightArrow.line.renderOrder = 999; // Behind label (label is 1000)
        // Configure material to render on top but respect cabinet occlusion
        if (
            rightArrow.line.material &&
            !Array.isArray(rightArrow.line.material)
        ) {
            rightArrow.line.material.depthTest = true; // Allow cabinets to occlude
            rightArrow.line.material.depthWrite = false; // Don't write to depth buffer
            rightArrow.line.material.transparent = true;
            rightArrow.line.material.opacity = 0.9; // Match label canvas opacity
            // Black color
            if (rightArrow.line.material instanceof THREE.LineBasicMaterial) {
                rightArrow.line.material.color.setHex(0x000000);
            }
        }
    }
    group.add(rightArrow);

    // Create distance label sprite at midpoint (of the full distance, not shortened line)
    const midpoint = new THREE.Vector3()
        .addVectors(start, end)
        .multiplyScalar(0.5);

    // Create canvas for distance text
    const canvas = document.createElement('canvas');
    const size = 128;
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d');
    if (context) {
        context.clearRect(0, 0, size, size);

        // Draw background (rounded rectangle)
        const paddingHorizontal = 35; // Reduced horizontal padding for 3-digit numbers
        const textWidth = size - paddingHorizontal * 2;
        const textHeight = 45;
        const x = paddingHorizontal;
        const y = size / 2 - textHeight / 2;
        const radius = 7;

        context.fillStyle = '#000000'; // Black background
        context.beginPath();
        context.moveTo(x + radius, y);
        context.lineTo(x + textWidth - radius, y);
        context.quadraticCurveTo(x + textWidth, y, x + textWidth, y + radius);
        context.lineTo(x + textWidth, y + textHeight - radius);
        context.quadraticCurveTo(
            x + textWidth,
            y + textHeight,
            x + textWidth - radius,
            y + textHeight
        );
        context.lineTo(x + radius, y + textHeight);
        context.quadraticCurveTo(x, y + textHeight, x, y + textHeight - radius);
        context.lineTo(x, y + radius);
        context.quadraticCurveTo(x, y, x + radius, y);
        context.closePath();
        context.fill();

        // Draw text
        context.fillStyle = 'white';
        context.font = 'bold 29px Arial';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(`${distance.toFixed(0)}`, size / 2, size / 2);
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    const spriteMaterial = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        sizeAttenuation: false,
    });

    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.position.copy(midpoint);
    sprite.scale.set(54, 54, 1); // 10% smaller for better proportions
    sprite.renderOrder = 1000;

    // Simple non-clickable label for distance measurement
    sprite.userData.measurementType = `cabinet-distance-${side}`;
    sprite.userData.side = side;

    // Only add the label sprite if distance is 1mm or greater
    // Use a small epsilon to account for floating point precision
    if (distance >= 0.9) {
        group.add(sprite);
    }

    return group;
};

/** World-axis sides for distance detection (left = -X, right = +X, front = +Z, back = -Z) */
export type CardinalSide = 'left' | 'right' | 'front' | 'back';

/**
 * Returns true if two boxes overlap in the Y axis (same height band).
 * @param {THREE.Box3} a - First bounding box
 * @param {THREE.Box3} b - Second bounding box
 * @return {boolean} true if the boxes overlap in Y
 */
function boxesOverlapInYCardinal(a: THREE.Box3, b: THREE.Box3): boolean {
    return boxesShareUsableYBand(a, b);
}

/**
 * Returns the gap distance from target to another box on a given cardinal side (world axes).
 * Left = -X, Right = +X, Front = +Z, Back = -Z.
 * @param {THREE.Box3} targetBox - Target cabinet bounding box
 * @param {THREE.Box3} otherBox - Other cabinet bounding box
 * @param {CardinalSide} side - Cardinal side to measure
 * @return {{ distance: number } | null} distance in scaled units, or null if other is not on that side
 */
function getCardinalDistance(
    targetBox: THREE.Box3,
    otherBox: THREE.Box3,
    side: CardinalSide
): {distance: number} | null {
    if (!boxesOverlapInYCardinal(targetBox, otherBox)) return null;
    let distance: number;
    switch (side) {
        case 'left':
            if (otherBox.max.x >= targetBox.min.x) return null;
            if (
                otherBox.max.z < targetBox.min.z ||
                otherBox.min.z > targetBox.max.z
            )
                return null;
            distance = targetBox.min.x - otherBox.max.x;
            break;
        case 'right':
            if (otherBox.min.x <= targetBox.max.x) return null;
            if (
                otherBox.max.z < targetBox.min.z ||
                otherBox.min.z > targetBox.max.z
            )
                return null;
            distance = otherBox.min.x - targetBox.max.x;
            break;
        case 'front':
            if (otherBox.min.z <= targetBox.max.z) return null;
            if (
                otherBox.max.x < targetBox.min.x ||
                otherBox.min.x > targetBox.max.x
            )
                return null;
            distance = otherBox.min.z - targetBox.max.z;
            break;
        case 'back':
            if (otherBox.max.z >= targetBox.min.z) return null;
            if (
                otherBox.max.x < targetBox.min.x ||
                otherBox.min.x > targetBox.max.x
            )
                return null;
            distance = targetBox.min.z - otherBox.max.z;
            break;
        default:
            return null;
    }
    return distance > 0 ? {distance} : null;
}

/**
 * Returns true if any cabinet lies between target and nearest on the given cardinal axis.
 * @param {THREE.Box3} targetBox - Target cabinet bounding box
 * @param {THREE.Box3} nearestBox - Nearest cabinet bounding box
 * @param {THREE.Group[]} allCabinets - All cabinets and placeholders in the scene
 * @param {THREE.Object3D} target - Target cabinet/placeholder
 * @param {THREE.Object3D} nearest - Nearest cabinet/placeholder
 * @param {CardinalSide} side - Cardinal side
 * @return {boolean} true if any cabinet is between target and nearest
 */
function hasCabinetBetweenCardinal(
    targetBox: THREE.Box3,
    nearestBox: THREE.Box3,
    allCabinets: THREE.Group[],
    target: THREE.Object3D,
    nearest: THREE.Object3D,
    side: CardinalSide
): boolean {
    const tolerance = 5;
    for (const cabinet of allCabinets) {
        // Identify by uuid: duplicated products share a job cabinet id and
        // placeholders use their own id scheme.
        if (cabinet.uuid === target.uuid || cabinet.uuid === nearest.uuid)
            continue;
        if (cabinet.userData.isTempMesh) continue;
        const box = computeWorldBoundsExcludingAnimatedDoorParts(cabinet);
        if (!boxesOverlapInYCardinal(targetBox, box)) continue;
        let between = false;
        switch (side) {
            case 'left':
                between =
                    box.max.x <= targetBox.min.x + tolerance &&
                    box.min.x >= nearestBox.max.x - tolerance &&
                    box.min.z < targetBox.max.z + tolerance &&
                    box.max.z > targetBox.min.z - tolerance;
                break;
            case 'right':
                between =
                    box.min.x >= targetBox.max.x - tolerance &&
                    box.max.x <= nearestBox.min.x + tolerance &&
                    box.min.z < targetBox.max.z + tolerance &&
                    box.max.z > targetBox.min.z - tolerance;
                break;
            case 'front':
                between =
                    box.min.z >= targetBox.max.z - tolerance &&
                    box.max.z <= nearestBox.min.z + tolerance &&
                    box.min.x < targetBox.max.x + tolerance &&
                    box.max.x > targetBox.min.x - tolerance;
                break;
            case 'back':
                between =
                    box.max.z <= targetBox.min.z + tolerance &&
                    box.min.z >= nearestBox.max.z - tolerance &&
                    box.min.x < targetBox.max.x + tolerance &&
                    box.max.x > targetBox.min.x - tolerance;
                break;
        }
        if (between) return true;
    }
    return false;
}

/**
 * Finds the nearest cabinet on each of the four world-axis sides (left, right, front, back).
 * Left = -X, Right = +X, Front = +Z, Back = -Z.
 * Placeholders (fridge/oven) are treated as neighbours like any cabinet.
 * @param {THREE.Group} targetCabinet - The target cabinet or placeholder to measure from
 * @param {THREE.Group[]} allCabinets - All cabinets and placeholders in the scene
 * @return {Object} Nearest cabinet and distance per side (left, right, front, back), or null when none
 */
export const findNearestCabinetsAllSides = (
    targetCabinet: THREE.Group,
    allCabinets: THREE.Group[]
): {
    left: {cabinet: THREE.Group; distance: number} | null;
    right: {cabinet: THREE.Group; distance: number} | null;
    front: {cabinet: THREE.Group; distance: number} | null;
    back: {cabinet: THREE.Group; distance: number} | null;
} => {
    const targetBox =
        computeWorldBoundsExcludingAnimatedDoorParts(targetCabinet);
    const sides: CardinalSide[] = ['left', 'right', 'front', 'back'];
    const result: {
        left: {cabinet: THREE.Group; distance: number} | null;
        right: {cabinet: THREE.Group; distance: number} | null;
        front: {cabinet: THREE.Group; distance: number} | null;
        back: {cabinet: THREE.Group; distance: number} | null;
    } = {
        left: null,
        right: null,
        front: null,
        back: null,
    };
    const minDist: Record<CardinalSide, number> = {
        left: Infinity,
        right: Infinity,
        front: Infinity,
        back: Infinity,
    };
    const nearest: Record<CardinalSide, THREE.Group | null> = {
        left: null,
        right: null,
        front: null,
        back: null,
    };

    const tryUpdateNearest = (
        side: CardinalSide,
        distance: number,
        cab: THREE.Group
    ): void => {
        switch (side) {
            case 'left':
                if (distance < minDist.left) {
                    minDist.left = distance;
                    nearest.left = cab;
                }
                break;
            case 'right':
                if (distance < minDist.right) {
                    minDist.right = distance;
                    nearest.right = cab;
                }
                break;
            case 'front':
                if (distance < minDist.front) {
                    minDist.front = distance;
                    nearest.front = cab;
                }
                break;
            case 'back':
                if (distance < minDist.back) {
                    minDist.back = distance;
                    nearest.back = cab;
                }
                break;
        }
    };

    for (const cabinet of allCabinets) {
        if (cabinet.uuid === targetCabinet.uuid) continue;
        if (cabinet.userData.isTempMesh) continue;
        const cabinetBox =
            computeWorldBoundsExcludingAnimatedDoorParts(cabinet);
        if (!boxesOverlapInYCardinal(targetBox, cabinetBox)) continue;

        for (const side of sides) {
            const out = getCardinalDistance(targetBox, cabinetBox, side);
            if (out && out.distance > 0) {
                tryUpdateNearest(side, out.distance, cabinet);
            }
        }
    }

    const clearNearestIfBlocked = (side: CardinalSide): void => {
        const nearestOnSide = nearest[`${side}`];
        if (!nearestOnSide) return;

        const isBlocked = hasCabinetBetweenCardinal(
            targetBox,
            computeWorldBoundsExcludingAnimatedDoorParts(nearestOnSide),
            allCabinets,
            targetCabinet,
            nearestOnSide,
            side
        );

        if (isBlocked) {
            nearest[`${side}`] = null;
        }
    };
    for (const side of sides) {
        clearNearestIfBlocked(side);
    }

    result.left =
        nearest.left !== null
            ? {cabinet: nearest.left, distance: minDist.left}
            : null;
    result.right =
        nearest.right !== null
            ? {cabinet: nearest.right, distance: minDist.right}
            : null;
    result.front =
        nearest.front !== null
            ? {cabinet: nearest.front, distance: minDist.front}
            : null;
    result.back =
        nearest.back !== null
            ? {cabinet: nearest.back, distance: minDist.back}
            : null;
    return result;
};

/**
 * Returns distance and side validity for one cabinet on one side (used by findNearest*).
 * @param {THREE.Box3} cabinetBox - The cabinet box to check distance for
 * @param {THREE.Box3} targetBox - The target cabinet box
 * @param {string} cabinetDirection - The direction the cabinet is facing
 * @param {boolean} isLShaped - Whether the cabinet is L-shaped
 * @param {'left' | 'right'} side - Which side to check
 * @return {{ distance: number, isOnCorrectSide: boolean } | null} null if cabinet should be skipped for this side
 */
function getDistanceAndSideForCabinet(
    cabinetBox: THREE.Box3,
    targetBox: THREE.Box3,
    cabinetDirection: string,
    isLShaped: boolean,
    side: 'left' | 'right'
): {distance: number; isOnCorrectSide: boolean} | null {
    if (!boxesShareUsableYBand(targetBox, cabinetBox)) {
        return null;
    }

    let distance: number;
    let isOnCorrectSide = false;

    if (
        (cabinetDirection === 'front' || cabinetDirection === 'back') &&
        isLShaped &&
        side === 'left'
    ) {
        const xOverlap =
            cabinetBox.min.x <= targetBox.max.x &&
            cabinetBox.max.x >= targetBox.min.x;
        if (!xOverlap) return null;
        if (cabinetDirection === 'front') {
            distance = cabinetBox.min.z - targetBox.max.z;
            isOnCorrectSide = cabinetBox.min.z > targetBox.max.z;
        } else {
            distance = targetBox.min.z - cabinetBox.max.z;
            isOnCorrectSide = cabinetBox.max.z < targetBox.min.z;
        }
    } else if (cabinetDirection === 'right' && isLShaped) {
        if (side === 'right') {
            distance = cabinetBox.min.z - targetBox.max.z;
            isOnCorrectSide = cabinetBox.min.z > targetBox.max.z;
        } else {
            distance = targetBox.min.x - cabinetBox.max.x;
            isOnCorrectSide = cabinetBox.max.x < targetBox.min.x;
        }
        const zOverlap =
            cabinetBox.min.z <= targetBox.max.z &&
            cabinetBox.max.z >= targetBox.min.z;
        isOnCorrectSide = isOnCorrectSide && zOverlap;
    } else if (cabinetDirection === 'left' && isLShaped) {
        if (side === 'right') {
            distance = cabinetBox.min.x - targetBox.max.x;
            isOnCorrectSide = cabinetBox.min.x > targetBox.max.x;
        } else {
            distance = cabinetBox.min.z - targetBox.max.z;
            isOnCorrectSide = cabinetBox.min.z > targetBox.max.z;
        }
        const zOverlap =
            cabinetBox.min.z <= targetBox.max.z &&
            cabinetBox.max.z >= targetBox.min.z;
        isOnCorrectSide = isOnCorrectSide && zOverlap;
    } else if (cabinetDirection === 'back' && isLShaped && side === 'right') {
        distance = targetBox.min.z - cabinetBox.max.z;
        isOnCorrectSide = cabinetBox.max.z < targetBox.min.z;
        const xOverlap =
            cabinetBox.min.x <= targetBox.max.x &&
            cabinetBox.max.x >= targetBox.min.x;
        isOnCorrectSide = isOnCorrectSide && xOverlap;
    } else if (cabinetDirection === 'front' || cabinetDirection === 'back') {
        if (side === 'right') {
            distance = cabinetBox.min.x - targetBox.max.x;
            isOnCorrectSide = cabinetBox.min.x > targetBox.max.x;
        } else {
            distance = targetBox.min.x - cabinetBox.max.x;
            isOnCorrectSide = cabinetBox.max.x < targetBox.min.x;
        }
        const zOverlap =
            cabinetBox.min.z <= targetBox.max.z &&
            cabinetBox.max.z >= targetBox.min.z;
        isOnCorrectSide = isOnCorrectSide && zOverlap;
    } else {
        if (side === 'right') {
            distance = cabinetBox.min.z - targetBox.max.z;
            isOnCorrectSide = cabinetBox.min.z > targetBox.max.z;
        } else {
            distance = targetBox.min.z - cabinetBox.max.z;
            isOnCorrectSide = cabinetBox.max.z < targetBox.min.z;
        }
        const xOverlap =
            cabinetBox.min.x <= targetBox.max.x &&
            cabinetBox.max.x >= targetBox.min.x;
        isOnCorrectSide = isOnCorrectSide && xOverlap;
    }
    return {distance, isOnCorrectSide};
}

/**
 * Returns true if any cabinet in allCabinets lies between target and nearest (used by findNearest*).
 * @param {THREE.Box3} targetBox - The target cabinet box
 * @param {THREE.Box3} nearestBox - The nearest cabinet box
 * @param {THREE.Group[]} allCabinets - All cabinets in the scene
 * @param {unknown} targetId - The ID of the target cabinet
 * @param {unknown} nearestId - The ID of the nearest cabinet
 * @param {'left' | 'right'} side - Which side to check
 * @param {string} cabinetDirection - The direction the cabinet is facing
 * @param {boolean} isLShaped - Whether the cabinet is L-shaped
 * @return {boolean} True if any cabinet in allCabinets lies between target and nearest
 */
function hasCabinetBetweenTargetAndNearest(
    targetBox: THREE.Box3,
    nearestBox: THREE.Box3,
    allCabinets: THREE.Group[],
    targetId: unknown,
    nearestId: unknown,
    side: 'left' | 'right',
    cabinetDirection: string,
    isLShaped: boolean
): boolean {
    const tolerance = 5;
    const isLShapedLeftFront =
        (cabinetDirection === 'front' || cabinetDirection === 'back') &&
        isLShaped &&
        side === 'left';
    const isLShapedRightLeft =
        cabinetDirection === 'right' && isLShaped && side === 'left';
    const isLShapedLeftRight =
        cabinetDirection === 'left' && isLShaped && side === 'right';
    const isLShapedBackRight =
        cabinetDirection === 'back' && isLShaped && side === 'right';

    for (const cabinet of allCabinets) {
        if (
            cabinet.userData.id === targetId ||
            cabinet.userData.id === nearestId
        ) {
            continue;
        }
        if (cabinet.userData.isTempMesh) continue;

        const cabinetBox =
            computeWorldBoundsExcludingAnimatedDoorParts(cabinet);
        if (!boxesShareUsableYBand(targetBox, cabinetBox)) continue;
        let isBetween = false;

        if (isLShapedLeftFront) {
            const hasXOverlap =
                cabinetBox.min.x <= targetBox.max.x + tolerance &&
                cabinetBox.max.x >= targetBox.min.x - tolerance;
            if (cabinetDirection === 'front') {
                isBetween =
                    hasXOverlap &&
                    cabinetBox.max.z >= targetBox.max.z - tolerance &&
                    cabinetBox.min.z <= nearestBox.min.z + tolerance;
            } else {
                isBetween =
                    hasXOverlap &&
                    cabinetBox.min.z <= targetBox.min.z + tolerance &&
                    cabinetBox.max.z >= nearestBox.max.z - tolerance;
            }
        } else if (isLShapedRightLeft) {
            const hasZOverlap =
                cabinetBox.min.z <= targetBox.max.z + tolerance &&
                cabinetBox.max.z >= targetBox.min.z - tolerance;
            isBetween =
                hasZOverlap &&
                cabinetBox.max.x <= targetBox.min.x + tolerance &&
                cabinetBox.min.x >= nearestBox.max.x - tolerance;
        } else if (isLShapedLeftRight) {
            const hasZOverlap =
                cabinetBox.min.z <= targetBox.max.z + tolerance &&
                cabinetBox.max.z >= targetBox.min.z - tolerance;
            isBetween =
                hasZOverlap &&
                cabinetBox.min.x >= targetBox.max.x - tolerance &&
                cabinetBox.max.x <= nearestBox.min.x + tolerance;
        } else if (isLShapedBackRight) {
            const hasXOverlap =
                cabinetBox.min.x <= targetBox.max.x + tolerance &&
                cabinetBox.max.x >= targetBox.min.x - tolerance;
            isBetween =
                hasXOverlap &&
                cabinetBox.max.z <= targetBox.min.z + tolerance &&
                cabinetBox.min.z >= nearestBox.max.z - tolerance;
        } else if (
            cabinetDirection === 'front' ||
            cabinetDirection === 'back'
        ) {
            const hasZOverlap =
                cabinetBox.min.z <= targetBox.max.z + tolerance &&
                cabinetBox.max.z >= targetBox.min.z - tolerance;
            if (hasZOverlap) {
                if (side === 'right') {
                    isBetween =
                        cabinetBox.min.x >= targetBox.max.x - tolerance &&
                        cabinetBox.max.x <= nearestBox.min.x + tolerance;
                } else {
                    isBetween =
                        cabinetBox.max.x <= targetBox.min.x + tolerance &&
                        cabinetBox.min.x >= nearestBox.max.x - tolerance;
                }
            }
        } else {
            const hasXOverlap =
                cabinetBox.min.x <= targetBox.max.x + tolerance &&
                cabinetBox.max.x >= targetBox.min.x - tolerance;
            if (hasXOverlap) {
                if (side === 'right') {
                    isBetween =
                        cabinetBox.min.z >= targetBox.max.z - tolerance &&
                        cabinetBox.max.z <= nearestBox.min.z + tolerance;
                } else {
                    isBetween =
                        cabinetBox.max.z <= targetBox.min.z + tolerance &&
                        cabinetBox.min.z >= nearestBox.max.z - tolerance;
                }
            }
        }
        if (isBetween) return true;
    }
    return false;
}

/**
 * Finds the nearest cabinet on a specific side of the target cabinet
 * @param {THREE.Group} targetCabinet - The target cabinet to measure from
 * @param {'left' | 'right'} side - Which side to check
 * @param {THREE.Group[]} allCabinets - All cabinets in the scene
 * @return {{cabinet: THREE.Group, distance: number} | null} Nearest cabinet and distance, or null
 */
export const findNearestCabinetOnSide = (
    targetCabinet: THREE.Group,
    side: 'left' | 'right',
    allCabinets: THREE.Group[]
): {cabinet: THREE.Group; distance: number} | null => {
    const targetBox =
        computeWorldBoundsExcludingAnimatedDoorParts(targetCabinet);
    const cabinetDirection =
        (targetCabinet.userData.currentDirection as string) || 'front';
    const isLShaped = Boolean(targetCabinet.userData.isLShapedProduct);

    let nearestCabinet: THREE.Group | null = null;
    let minDistance = Infinity;

    for (const cabinet of allCabinets) {
        if (cabinet.userData.id === targetCabinet.userData.id) continue;
        if (cabinet.userData.isTempMesh) continue;

        const cabinetBox =
            computeWorldBoundsExcludingAnimatedDoorParts(cabinet);
        const result = getDistanceAndSideForCabinet(
            cabinetBox,
            targetBox,
            cabinetDirection,
            isLShaped,
            side
        );
        if (
            !result ||
            !result.isOnCorrectSide ||
            result.distance <= 0 ||
            result.distance >= minDistance
        ) {
            continue;
        }
        minDistance = result.distance;
        nearestCabinet = cabinet;
    }

    if (nearestCabinet) {
        const nearestBox =
            computeWorldBoundsExcludingAnimatedDoorParts(nearestCabinet);
        if (
            hasCabinetBetweenTargetAndNearest(
                targetBox,
                nearestBox,
                allCabinets,
                targetCabinet.userData.id,
                nearestCabinet.userData.id,
                side,
                cabinetDirection,
                isLShaped
            )
        ) {
            return null;
        }
    }

    return nearestCabinet
        ? {cabinet: nearestCabinet, distance: minDistance}
        : null;
};

/**
 * Finds the nearest cabinets on both left and right in a single pass over allCabinets.
 * @param {THREE.Group} targetCabinet - The target cabinet to measure from
 * @param {THREE.Group[]} allCabinets - All cabinets in the scene
 * @return {Object} The nearest cabinets and distances, or null if no nearest cabinet is found
 */
export const findNearestCabinetsBothSides = (
    targetCabinet: THREE.Group,
    allCabinets: THREE.Group[]
): {
    left: {cabinet: THREE.Group; distance: number} | null;
    right: {cabinet: THREE.Group; distance: number} | null;
} => {
    const targetBox =
        computeWorldBoundsExcludingAnimatedDoorParts(targetCabinet);
    const cabinetDirection =
        (targetCabinet.userData.currentDirection as string) || 'front';
    const isLShaped = Boolean(targetCabinet.userData.isLShapedProduct);

    let leftNearest: THREE.Group | null = null;
    let leftMinDistance = Infinity;
    let rightNearest: THREE.Group | null = null;
    let rightMinDistance = Infinity;

    for (const cabinet of allCabinets) {
        if (cabinet.userData.id === targetCabinet.userData.id) continue;
        if (cabinet.userData.isTempMesh) continue;

        const cabinetBox =
            computeWorldBoundsExcludingAnimatedDoorParts(cabinet);

        const leftResult = getDistanceAndSideForCabinet(
            cabinetBox,
            targetBox,
            cabinetDirection,
            isLShaped,
            'left'
        );
        if (
            leftResult &&
            leftResult.distance > 0 &&
            leftResult.distance < leftMinDistance
        ) {
            leftMinDistance = leftResult.distance;
            leftNearest = cabinet;
        }

        const rightResult = getDistanceAndSideForCabinet(
            cabinetBox,
            targetBox,
            cabinetDirection,
            isLShaped,
            'right'
        );
        if (
            rightResult &&
            rightResult.isOnCorrectSide &&
            rightResult.distance > 0 &&
            rightResult.distance < rightMinDistance
        ) {
            rightMinDistance = rightResult.distance;
            rightNearest = cabinet;
        }
    }

    if (leftNearest) {
        const nearestBox =
            computeWorldBoundsExcludingAnimatedDoorParts(leftNearest);
        if (
            hasCabinetBetweenTargetAndNearest(
                targetBox,
                nearestBox,
                allCabinets,
                targetCabinet.userData.id,
                leftNearest.userData.id,
                'left',
                cabinetDirection,
                isLShaped
            )
        ) {
            leftNearest = null;
        }
    }

    if (rightNearest) {
        const nearestBox =
            computeWorldBoundsExcludingAnimatedDoorParts(rightNearest);
        if (
            hasCabinetBetweenTargetAndNearest(
                targetBox,
                nearestBox,
                allCabinets,
                targetCabinet.userData.id,
                rightNearest.userData.id,
                'right',
                cabinetDirection,
                isLShaped
            )
        ) {
            rightNearest = null;
        }
    }

    return {
        left:
            leftNearest !== null
                ? {cabinet: leftNearest, distance: leftMinDistance}
                : null,
        right:
            rightNearest !== null
                ? {cabinet: rightNearest, distance: rightMinDistance}
                : null,
    };
};

/**
 * Side button click handling types
 */
export interface SideButtonRefs {
    leftButton: React.MutableRefObject<THREE.Sprite | null>;
    rightButton: React.MutableRefObject<THREE.Sprite | null>;
    buttonGroup: React.MutableRefObject<THREE.Group | null>;
}

export interface SideButtonCallbacks {
    onButtonClick?: (
        side: 'left' | 'right',
        cabinet: THREE.Group,
        position?: {x: number; y: number}
    ) => void;
}

/**
 * Checks if the closest intersection from raycasting is a side button
 * Used to prevent dragging when clicking on side buttons
 * @param {THREE.Intersection[]} allIntersects - All intersections from raycasting
 * @param {THREE.Intersection | undefined} closestIntersect - The closest intersection
 * @param {SideButtonRefs} sideButtonRefs - References to side button sprites
 * @return {boolean} True if the closest intersection is a side button
 */
export const isSideButtonClick = (
    allIntersects: THREE.Intersection[],
    closestIntersect: THREE.Intersection | undefined,
    sideButtonRefs: SideButtonRefs | null
): boolean => {
    if (!sideButtonRefs || !sideButtonRefs.buttonGroup.current?.visible) {
        return false;
    }

    if (!closestIntersect) {
        return false;
    }

    // Check all intersections to see if a button is the closest/clicked object
    const buttonIntersects = allIntersects.filter((intersect) => {
        // Check if this intersection is one of our side buttons
        return (
            intersect.object === sideButtonRefs.leftButton.current ||
            intersect.object === sideButtonRefs.rightButton.current
        );
    });

    // If a button is the closest intersection, it's a side button click
    return (
        buttonIntersects.length > 0 && buttonIntersects[0] === closestIntersect
    );
};

/**
 * Checks if any side button is intersected in the raycast results
 * This is more lenient than isSideButtonClick and checks if ANY button is hit,
 * not just the closest one. Useful when buttons might be occluded by cabinets.
 * @param {THREE.Intersection[]} allIntersects - All intersections from raycasting
 * @param {SideButtonRefs} sideButtonRefs - References to side button sprites
 * @return {boolean} True if any side button is intersected
 */
export const isAnySideButtonIntersected = (
    allIntersects: THREE.Intersection[],
    sideButtonRefs: SideButtonRefs | null
): boolean => {
    if (!sideButtonRefs || !sideButtonRefs.buttonGroup.current?.visible) {
        return false;
    }

    // Check all intersections to see if any button is intersected
    const buttonIntersects = allIntersects.filter((intersect) => {
        // Check if this intersection is one of our side buttons
        return (
            intersect.object === sideButtonRefs.leftButton.current ||
            intersect.object === sideButtonRefs.rightButton.current
        );
    });

    // If any button is intersected, return true
    return buttonIntersects.length > 0;
};

/**
 * Tracks mouse movement for drag detection on side buttons
 * @param {MouseEvent} event - The mouse move event
 * @param {Object | null} mouseDownPosition - The mouse down position with x and y
 * @return {boolean} True if mouse moved significantly (drag detected)
 */
export const trackSideButtonMouseMove = (
    event: MouseEvent,
    mouseDownPosition: {x: number; y: number} | null
): boolean => {
    if (!mouseDownPosition) {
        return false;
    }

    const moveDistance = Math.sqrt(
        Math.pow(event.clientX - mouseDownPosition.x, 2) +
            Math.pow(event.clientY - mouseDownPosition.y, 2)
    );

    // If mouse moved more than 5 pixels, consider it a drag
    return moveDistance > 5;
};

/**
 * Handles side button click detection on mouse up
 * @param {MouseEvent} event - The mouse up event
 * @param {THREE.OrthographicCamera} camera - The camera for raycasting
 * @param {HTMLDivElement} container - The container element
 * @param {SideButtonRefs | null} sideButtonRefs - References to side button sprites
 * @param {CabinetMesh | null} selectedCabinet - The currently selected cabinet
 * @param {SideButtonCallbacks | null} callbacks - The button click callbacks
 * @param {boolean} mouseMoved - Whether the mouse moved significantly (drag detected)
 * @return {Object | null} The clicked side and position, or null if not clicked
 */
export const handleSideButtonClick = (
    event: MouseEvent,
    camera: THREE.OrthographicCamera,
    container: HTMLDivElement,
    sideButtonRefs: SideButtonRefs | null,
    selectedCabinet: CabinetMesh | null,
    callbacks: SideButtonCallbacks | null,
    mouseMoved: boolean
): {side: 'left' | 'right'; position: {x: number; y: number}} | null => {
    // Only process left mouse button
    if (event.button !== 0) {
        return null;
    }

    // If mouse moved significantly, it was a drag, not a click
    if (mouseMoved) {
        return null;
    }

    if (
        !sideButtonRefs ||
        !callbacks ||
        !selectedCabinet ||
        !sideButtonRefs.buttonGroup.current?.visible ||
        !sideButtonRefs.leftButton.current ||
        !sideButtonRefs.rightButton.current
    ) {
        return null;
    }

    // Calculate mouse position
    const dimension = container.getBoundingClientRect();
    const mouseX = ((event.clientX - dimension.left) / dimension.width) * 2 - 1;
    const mouseY =
        (-(event.clientY - dimension.top) / dimension.height) * 2 + 1;

    const buttonRaycaster = new THREE.Raycaster();
    buttonRaycaster.setFromCamera(new THREE.Vector2(mouseX, mouseY), camera);

    // Check buttons if they exist and button group is visible
    // Only skip if button is explicitly set to false
    const buttonsToCheck: THREE.Sprite[] = [];

    if (
        sideButtonRefs.leftButton.current &&
        sideButtonRefs.leftButton.current.visible !== false
    ) {
        // Update matrix before raycasting
        sideButtonRefs.leftButton.current.updateMatrixWorld(true);
        buttonsToCheck.push(sideButtonRefs.leftButton.current);
    }
    if (
        sideButtonRefs.rightButton.current &&
        sideButtonRefs.rightButton.current.visible !== false
    ) {
        // Update matrix before raycasting
        sideButtonRefs.rightButton.current.updateMatrixWorld(true);
        buttonsToCheck.push(sideButtonRefs.rightButton.current);
    }

    // Update button group matrix as well
    if (sideButtonRefs.buttonGroup.current) {
        sideButtonRefs.buttonGroup.current.updateMatrixWorld(true);
    }

    // Check intersections with buttons - use recursive search to find all intersections
    // This allows detection even when cabinets are in front (buttons have depthTest: false)
    const buttonIntersects = buttonRaycaster.intersectObjects(
        buttonsToCheck,
        false
    );

    // Find the closest button intersection (buttons render on top, so closest should be the button)
    // But also check if any button is intersected, even if occluded
    let closestButtonIntersect: THREE.Intersection | null = null;
    for (const intersect of buttonIntersects) {
        if (
            intersect.object === sideButtonRefs.leftButton.current ||
            intersect.object === sideButtonRefs.rightButton.current
        ) {
            if (
                !closestButtonIntersect ||
                intersect.distance < closestButtonIntersect.distance
            ) {
                closestButtonIntersect = intersect;
            }
        }
    }

    if (closestButtonIntersect) {
        const intersected = closestButtonIntersect.object as THREE.Sprite & {
            userData: {side: 'left' | 'right'};
        };
        const side = intersected.userData.side;

        // Trigger callback with click position
        callbacks.onButtonClick?.(side, selectedCabinet, {
            x: event.clientX,
            y: event.clientY,
        });

        return {
            side,
            position: {x: event.clientX, y: event.clientY},
        };
    }

    return null;
};

/**
 * Interface for side button groups and refs
 */
export interface SideButtonGroups {
    buttonGroup: THREE.Group;
    leftButton: THREE.Sprite;
    rightButton: THREE.Sprite;
}

/**
 * Disposes all distance lines from the distance lines group
 * Properly cleans up geometries, materials, textures, and ArrowHelper components
 * @param {THREE.Group} distanceLinesGroup - The group containing distance lines
 */
export const disposeDistanceLines = (distanceLinesGroup: THREE.Group): void => {
    while (distanceLinesGroup.children.length > 0) {
        const child = distanceLinesGroup.children[0];
        distanceLinesGroup.remove(child);
        child.traverse((obj) => {
            if (obj instanceof THREE.Mesh || obj instanceof THREE.Line) {
                const geometry = obj.geometry as
                    THREE.BufferGeometry | undefined;
                if (geometry) {
                    geometry.dispose();
                }
                const material = obj.material as
                    THREE.Material | THREE.Material[] | undefined;
                if (Array.isArray(material)) {
                    material.forEach((m) => {
                        if (m) {
                            m.dispose();
                        }
                    });
                } else if (material) {
                    material.dispose();
                }
            }
            if (obj instanceof THREE.Sprite && obj.material) {
                if (obj.material.map) {
                    obj.material.map.dispose();
                }
                obj.material.dispose();
            }
            if (obj instanceof THREE.ArrowHelper) {
                if (obj.cone) {
                    if (obj.cone.geometry) {
                        obj.cone.geometry.dispose();
                    }
                    const coneMaterial = obj.cone.material;
                    if (Array.isArray(coneMaterial)) {
                        coneMaterial.forEach((m) => {
                            if (m) {
                                m.dispose();
                            }
                        });
                    } else if (coneMaterial) {
                        coneMaterial.dispose();
                    }
                }
                if (obj.line) {
                    if (obj.line.geometry) {
                        obj.line.geometry.dispose();
                    }
                    const lineMaterial = obj.line.material;
                    if (Array.isArray(lineMaterial)) {
                        lineMaterial.forEach((m) => {
                            if (m) {
                                m.dispose();
                            }
                        });
                    } else if (lineMaterial) {
                        lineMaterial.dispose();
                    }
                }
            }
        });
    }
};

/**
 * Sets up and updates distance lines for cabinets
 * This function handles the creation and updating of distance measurement lines
 * @param {THREE.Scene} scene - The scene to add distance lines to
 * @param {CabinetMesh} selectedCabinetGroup - The selected cabinet or placeholder
 * @param {CabinetMesh[]} allCabinets - All cabinets and placeholders in the scene
 * @param {THREE.Group | null} existingGroup - Existing distance lines group to reuse (null for first creation)
 * @return {THREE.Group} The distance lines group
 */
export const setupDistanceLines = (
    scene: THREE.Scene,
    selectedCabinetGroup: CabinetMesh,
    allCabinets: CabinetMesh[],
    existingGroup: THREE.Group | null = null
): THREE.Group => {
    // Create or reuse distance lines group (typed so it's never null after this)
    const distanceLinesGroup: THREE.Group =
        existingGroup ??
        (() => {
            const group = new THREE.Group();
            group.name = 'distanceLines';
            scene.add(group);
            return group;
        })();

    if (distanceLinesGroup.parent !== scene) {
        distanceLinesGroup.removeFromParent();
        scene.add(distanceLinesGroup);
    }

    // Get cabinet bounding box for line positioning
    const targetBox =
        computeWorldBoundsExcludingAnimatedDoorParts(selectedCabinetGroup);

    // Find nearest cabinet on each world-axis side (left, right, front, back)
    const nearestBySide = findNearestCabinetsAllSides(
        selectedCabinetGroup,
        allCabinets
    );

    // Clear existing distance lines
    disposeDistanceLines(distanceLinesGroup);

    const scale = getScale();

    // Draw a distance line for one cardinal side when a nearby cabinet is in range
    const drawLineForSide = (
        nearest: {cabinet: THREE.Group; distance: number} | null,
        side: CardinalSide
    ) => {
        if (
            !nearest ||
            nearest.distance >= MAX_DISTANCE_TO_SHOW_LINE ||
            nearest.distance <= MIN_DISTANCE_TO_SHOW_LINE
        ) {
            return;
        }

        const nearestBox = computeWorldBoundsExcludingAnimatedDoorParts(
            nearest.cabinet
        );
        const lineY = Math.min(targetBox.max.y, nearestBox.max.y);

        let startPoint: THREE.Vector3;
        let endPoint: THREE.Vector3;

        switch (side) {
            case 'left':
                // Line along X: target left face (min.x) to nearest right face (max.x)
                const leftOverlapMinZ = Math.max(
                    targetBox.min.z,
                    nearestBox.min.z
                );
                const leftOverlapMaxZ = Math.min(
                    targetBox.max.z,
                    nearestBox.max.z
                );
                const leftMiddleZ =
                    leftOverlapMinZ < leftOverlapMaxZ
                        ? (leftOverlapMinZ + leftOverlapMaxZ) / 2
                        : targetBox.min.z;
                startPoint = new THREE.Vector3(
                    targetBox.min.x,
                    lineY,
                    leftMiddleZ
                );
                endPoint = new THREE.Vector3(
                    nearestBox.max.x,
                    lineY,
                    leftMiddleZ
                );
                break;
            case 'right':
                const rightOverlapMinZ = Math.max(
                    targetBox.min.z,
                    nearestBox.min.z
                );
                const rightOverlapMaxZ = Math.min(
                    targetBox.max.z,
                    nearestBox.max.z
                );
                const rightMiddleZ =
                    rightOverlapMinZ < rightOverlapMaxZ
                        ? (rightOverlapMinZ + rightOverlapMaxZ) / 2
                        : targetBox.max.z;
                startPoint = new THREE.Vector3(
                    targetBox.max.x,
                    lineY,
                    rightMiddleZ
                );
                endPoint = new THREE.Vector3(
                    nearestBox.min.x,
                    lineY,
                    rightMiddleZ
                );
                break;
            case 'front':
                const frontOverlapMinX = Math.max(
                    targetBox.min.x,
                    nearestBox.min.x
                );
                const frontOverlapMaxX = Math.min(
                    targetBox.max.x,
                    nearestBox.max.x
                );
                const frontMiddleX =
                    frontOverlapMinX < frontOverlapMaxX
                        ? (frontOverlapMinX + frontOverlapMaxX) / 2
                        : targetBox.max.x;
                startPoint = new THREE.Vector3(
                    frontMiddleX,
                    lineY,
                    targetBox.max.z
                );
                endPoint = new THREE.Vector3(
                    frontMiddleX,
                    lineY,
                    nearestBox.min.z
                );
                break;
            case 'back':
                const backOverlapMinX = Math.max(
                    targetBox.min.x,
                    nearestBox.min.x
                );
                const backOverlapMaxX = Math.min(
                    targetBox.max.x,
                    nearestBox.max.x
                );
                const backMiddleX =
                    backOverlapMinX < backOverlapMaxX
                        ? (backOverlapMinX + backOverlapMaxX) / 2
                        : targetBox.min.x;
                startPoint = new THREE.Vector3(
                    backMiddleX,
                    lineY,
                    targetBox.min.z
                );
                endPoint = new THREE.Vector3(
                    backMiddleX,
                    lineY,
                    nearestBox.max.z
                );
                break;
        }

        const actualDistanceMm = nearest.distance / scale;
        const distanceLine = createDistanceLine(
            startPoint,
            endPoint,
            actualDistanceMm,
            side
        );
        distanceLinesGroup.add(distanceLine);
    };

    // Draw distance lines for all four sides when a nearby cabinet is in range
    drawLineForSide(nearestBySide.left, 'left');
    drawLineForSide(nearestBySide.right, 'right');
    drawLineForSide(nearestBySide.front, 'front');
    drawLineForSide(nearestBySide.back, 'back');

    return distanceLinesGroup;
};

/**
 * Rebuilds cabinet-to-cabinet distance lines from live mesh refs.
 * Same path as the layout hook: world bounds on the Three.js groups, not Redux.
 * No-ops when the ruler is off (no `distanceLines` group in the scene).
 * @param {THREE.Scene} scene - Scene that owns the distance-lines group
 * @param {CabinetMesh} selectedCabinetGroup - Selected cabinet or placeholder
 * @param {CabinetMesh[]} allItems - Cabinets plus placeholders from list refs
 * @return {THREE.Group | null} Updated group, or null when there is nothing to refresh
 */
export const refreshDistanceLines = (
    scene: THREE.Scene,
    selectedCabinetGroup: CabinetMesh,
    allItems: CabinetMesh[]
): THREE.Group | null => {
    const existing = scene.children.find(
        (child): child is THREE.Group =>
            child instanceof THREE.Group && child.name === 'distanceLines'
    );
    if (!existing) {
        return null;
    }

    return setupDistanceLines(scene, selectedCabinetGroup, allItems, existing);
};

/**
 * Sets up and updates side buttons for cabinets
 * Similar to setupMeasurements, this function manages side button creation and updates
 * @param {THREE.Scene} scene - The scene to add the button group to
 * @param {CabinetMesh | null} selectedCabinetGroup - The currently selected cabinet
 * @param {CabinetMesh[]} allCabinets - All cabinets in the scene
 * @param {THREE.OrthographicCamera} camera - The camera for occlusion checks
 * @param {SideButtonGroups | null} existingGroups - Existing button groups to update (null for first creation)
 * @param {boolean} hideButtons - hide buttons override
 * @return {SideButtonGroups | null} The side button groups, or null if buttons should be hidden
 */
export const setupSideButtons = (
    scene: THREE.Scene,
    selectedCabinetGroup: CabinetMesh | null,
    allCabinets: CabinetMesh[],
    camera: THREE.OrthographicCamera,
    existingGroups: SideButtonGroups | null,
    hideButtons = false
): SideButtonGroups | null => {
    if (!scene) {
        if (existingGroups) {
            existingGroups.buttonGroup.visible = false;
            existingGroups.leftButton.visible = false;
            existingGroups.rightButton.visible = false;
        }
        return existingGroups;
    }

    // Check if buttons should be hidden
    if (!selectedCabinetGroup) {
        if (existingGroups) {
            existingGroups.buttonGroup.visible = false;
            existingGroups.leftButton.visible = false;
            existingGroups.rightButton.visible = false;
        }

        return existingGroups;
    }

    const lShapedValues = selectedCabinetGroup.userData
        .values as TargetCabinetValues;
    const scaleFactor = getScale();

    const cabinetProps = {
        id: selectedCabinetGroup.userData.id,
        isPanelProduct: !!selectedCabinetGroup.userData.isPanelProduct,
        isVerminProduct: !!selectedCabinetGroup.userData.isVerminProduct,
        isFillerProduct: !!selectedCabinetGroup.userData.isFillerProduct,
        isLShapePanel: !!selectedCabinetGroup.userData.isLShapePanel,
        isPanelProductWithWidth:
            !!selectedCabinetGroup.userData.isPanelProductWithWidth,
        isPanelProductWithLength:
            !!selectedCabinetGroup.userData.isPanelProductWithLength,
        isNonManualPanel: !!selectedCabinetGroup.userData.isNonManualPanel,
        isKeyShapePanel: !!selectedCabinetGroup.userData.isKeyShapePanel,
        isManualPanel: !!selectedCabinetGroup.userData.isManualPanel,
        direction: selectedCabinetGroup.userData.currentDirection || 'front',
        isUShapedPanel: selectedCabinetGroup.userData.isUShapedPanel,
        isLShapedProduct: !!selectedCabinetGroup.userData.isLShapedProduct,
        cabinet_width_depth: getScaledLShapedLeftDepth(
            lShapedValues,
            scaleFactor
        ),
        cabinet_length_depth: getScaledLShapedRightDepth(
            lShapedValues,
            scaleFactor
        ),
        isDrawerFace:
            selectedCabinetGroup.userData.values.type_name === 'Drawer Face',
    };

    const shouldHideButtons =
        cabinetProps.isPanelProduct ||
        cabinetProps.isKeyShapePanel ||
        cabinetProps.isNonManualPanel ||
        cabinetProps.isLShapePanel ||
        cabinetProps.isVerminProduct ||
        cabinetProps.isFillerProduct ||
        cabinetProps.isPanelProductWithWidth ||
        cabinetProps.isPanelProductWithLength ||
        cabinetProps.isUShapedPanel ||
        cabinetProps.isManualPanel ||
        cabinetProps.isDrawerFace ||
        Boolean(selectedCabinetGroup.userData.isPlaceholder) ||
        hideButtons;
    hideButtons;

    // Create groups if they don't exist
    let buttonGroup: THREE.Group;
    let leftButton: THREE.Sprite;
    let rightButton: THREE.Sprite;

    if (existingGroups) {
        buttonGroup = existingGroups.buttonGroup;
        leftButton = existingGroups.leftButton;
        rightButton = existingGroups.rightButton;
        // After room rebuild, CameraSetup.setupScene() clears the scene; refs still
        // point at orphaned groups that must be re-attached to render.
        if (buttonGroup.parent !== scene) {
            buttonGroup.removeFromParent();
            scene.add(buttonGroup);
        }
    } else {
        buttonGroup = new THREE.Group();
        buttonGroup.name = 'addCabinetButtons';
        scene.add(buttonGroup);

        leftButton = createButtonSprite();
        leftButton.name = 'leftAddButton';
        leftButton.userData.side = 'left';
        leftButton.visible = false;
        // Ensure button is properly set up for raycasting
        leftButton.updateMatrixWorld(true);
        buttonGroup.add(leftButton);

        rightButton = createButtonSprite();
        rightButton.name = 'rightAddButton';
        rightButton.userData.side = 'right';
        rightButton.visible = false;
        // Ensure button is properly set up for raycasting
        rightButton.updateMatrixWorld(true);
        buttonGroup.add(rightButton);

        // Ensure button group is properly set up
        buttonGroup.updateMatrixWorld(true);
    }

    // Hide buttons if needed (but still calculate distances for distance lines)
    if (shouldHideButtons) {
        buttonGroup.visible = false;
        leftButton.visible = false;
        rightButton.visible = false;
        // Continue to calculate distances and draw distance lines even when buttons are hidden
    }

    // Verify the selected cabinet still exists in the list
    const cabinetStillExists = allCabinets.some(
        (cab) => cab.uuid === selectedCabinetGroup.uuid
    );

    if (!cabinetStillExists) {
        buttonGroup.visible = false;
        leftButton.visible = false;
        rightButton.visible = false;
        return {buttonGroup, leftButton, rightButton};
    }

    // Get cabinet bounding box to determine actual side positions
    // Reuse this box for multiple checks to avoid recalculating
    const cabinetBox =
        computeWorldBoundsExcludingAnimatedDoorParts(selectedCabinetGroup);
    const targetBox = cabinetBox; // Alias for clarity in overlap checks
    const direction = cabinetProps.direction;
    const sideOffset = 0;

    // Only calculate button-specific logic if buttons should be shown
    if (!shouldHideButtons) {
        const analysis = analyzeSideButtons(
            selectedCabinetGroup,
            targetBox,
            allCabinets,
            direction,
            cabinetProps.isLShapedProduct,
            cabinetProps.cabinet_width_depth
        );
        const {leftDistance, rightDistance, leftOccupied, rightOccupied} =
            analysis;
        let {leftHasOverlap, rightHasOverlap} = analysis;

        if (leftDistance !== null && leftDistance < 0) leftHasOverlap = true;
        if (rightDistance !== null && rightDistance < 0) rightHasOverlap = true;

        // Calculate button positions based on cabinet direction
        let leftButtonPos: THREE.Vector3;
        let rightButtonPos: THREE.Vector3;

        if (direction === 'front') {
            if (cabinetProps.isLShapedProduct && direction === 'front') {
                const centerY = (cabinetBox.min.y + cabinetBox.max.y) / 2;
                leftButtonPos = new THREE.Vector3(
                    cabinetBox.min.x + cabinetProps.cabinet_width_depth / 2,
                    centerY,
                    cabinetBox.max.z
                );
                rightButtonPos = new THREE.Vector3(
                    cabinetBox.max.x,
                    centerY,
                    cabinetBox.min.z + cabinetProps.cabinet_length_depth / 2
                );
            } else {
                const centerY = (cabinetBox.min.y + cabinetBox.max.y) / 2;
                const centerZ = (cabinetBox.min.z + cabinetBox.max.z) / 2;

                leftButtonPos = new THREE.Vector3(
                    cabinetBox.min.x,
                    centerY,
                    centerZ
                );
                rightButtonPos = new THREE.Vector3(
                    cabinetBox.max.x,
                    centerY,
                    centerZ
                );
            }
        } else if (direction === 'right') {
            const centerX = (cabinetBox.min.x + cabinetBox.max.x) / 2;
            const centerY = (cabinetBox.min.y + cabinetBox.max.y) / 2;

            // L-shaped right: left button on the wing — in front of the wing (min.x), at wing depth
            if (cabinetProps.isLShapedProduct) {
                leftButtonPos = new THREE.Vector3(
                    cabinetBox.min.x,
                    centerY,
                    cabinetBox.min.z + cabinetProps.cabinet_width_depth / 2
                );
                rightButtonPos = new THREE.Vector3(
                    cabinetBox.max.x - cabinetProps.cabinet_length_depth / 2,
                    centerY,
                    cabinetBox.max.z
                );
            } else {
                leftButtonPos = new THREE.Vector3(
                    centerX,
                    centerY,
                    cabinetBox.min.z - sideOffset
                );
                rightButtonPos = new THREE.Vector3(
                    centerX,
                    centerY,
                    cabinetBox.max.z + sideOffset
                );
            }
        } else if (direction === 'left') {
            const centerX = (cabinetBox.min.x + cabinetBox.max.x) / 2;
            const centerY = (cabinetBox.min.y + cabinetBox.max.y) / 2;

            leftButtonPos = new THREE.Vector3(
                centerX,
                centerY,
                cabinetBox.min.z - sideOffset
            );
            rightButtonPos = new THREE.Vector3(
                centerX,
                centerY,
                cabinetBox.max.z + sideOffset
            );
            if (cabinetProps.isLShapedProduct) {
                rightButtonPos = new THREE.Vector3(
                    cabinetBox.max.x,
                    centerY,
                    cabinetBox.max.z - cabinetProps.cabinet_width_depth / 2
                );

                leftButtonPos = new THREE.Vector3(
                    cabinetBox.min.x + cabinetProps.cabinet_length_depth / 2,
                    centerY,
                    cabinetBox.min.z - sideOffset
                );
            }
        } else if (direction === 'back') {
            if (cabinetProps.isLShapedProduct) {
                const centerY = (cabinetBox.min.y + cabinetBox.max.y) / 2;

                leftButtonPos = new THREE.Vector3(
                    cabinetBox.min.x,
                    centerY,
                    cabinetBox.max.z - cabinetProps.cabinet_length_depth / 2
                );
                rightButtonPos = new THREE.Vector3(
                    cabinetBox.max.x - cabinetProps.cabinet_width_depth / 2,
                    centerY,
                    cabinetBox.min.z
                );
            } else {
                const centerY = (cabinetBox.min.y + cabinetBox.max.y) / 2;
                const centerZ = (cabinetBox.min.z + cabinetBox.max.z) / 2;

                leftButtonPos = new THREE.Vector3(
                    cabinetBox.min.x,
                    centerY,
                    centerZ
                );
                rightButtonPos = new THREE.Vector3(
                    cabinetBox.max.x,
                    centerY,
                    centerZ
                );
            }
        } else {
            const centerY = (cabinetBox.min.y + cabinetBox.max.y) / 2;
            const centerZ = (cabinetBox.min.z + cabinetBox.max.z) / 2;
            leftButtonPos = new THREE.Vector3(
                cabinetBox.min.x - 40,
                centerY,
                centerZ
            );
            rightButtonPos = new THREE.Vector3(
                cabinetBox.max.x - 35,
                centerY,
                centerZ
            );
        }

        leftButton.position.copy(leftButtonPos);
        rightButton.position.copy(rightButtonPos);

        // Update matrices for proper raycasting
        leftButton.updateMatrixWorld(true);
        rightButton.updateMatrixWorld(true);
        buttonGroup.updateMatrixWorld(true);

        // OPTIMIZATION: Only compute expensive hasSpaceOnSide if we might show the button
        // This defers the expensive productClamping calls until after quick checks
        const DISTANCE_THRESHOLD_19MM = 3.8;
        const DISTANCE_TOLERANCE = 0.01;

        const leftHasEnoughDistance =
            !leftHasOverlap &&
            (leftDistance === null ||
                leftDistance >= DISTANCE_THRESHOLD_19MM - DISTANCE_TOLERANCE);
        const rightHasEnoughDistance =
            !rightHasOverlap &&
            (rightDistance === null ||
                rightDistance >= DISTANCE_THRESHOLD_19MM - DISTANCE_TOLERANCE);

        const leftSnapped =
            leftDistance !== null &&
            leftDistance < DISTANCE_THRESHOLD_19MM &&
            leftOccupied;

        const rightSnapped =
            rightDistance !== null &&
            rightDistance < DISTANCE_THRESHOLD_19MM &&
            rightOccupied;

        const leftVisible =
            !leftSnapped &&
            !leftHasOverlap &&
            (leftHasEnoughDistance || !leftOccupied);

        const rightVisible =
            !rightSnapped &&
            !rightHasOverlap &&
            (rightHasEnoughDistance || !rightOccupied);

        // Update button visibility
        if (leftVisible || rightVisible) {
            buttonGroup.visible = true;
        }

        leftButton.visible = leftVisible;
        rightButton.visible = rightVisible;

        // Only compute occlusion if buttons should be shown and are visible
        // OPTIMIZATION: Only compute occlusion if buttons are visible
        // This avoids expensive raycasting when buttons are hidden
        let leftOpacity = 1.0;
        let rightOpacity = 1.0;

        if (leftVisible || rightVisible) {
            // Update occlusion (only if at least one button is visible)
            const raycaster = new THREE.Raycaster();
            const occlusionCabinets = allCabinets.filter(
                (cabinet) => !cabinet.userData.isTempMesh
            );

            // Only check occlusion for visible buttons
            if (leftVisible) {
                const buttonPos = leftButton.position;
                const dir = new THREE.Vector3()
                    .subVectors(buttonPos, camera.position)
                    .normalize();
                raycaster.set(camera.position, dir);
                const distance = camera.position.distanceTo(buttonPos);
                const intersections = raycaster.intersectObjects(
                    occlusionCabinets,
                    true
                );
                const leftBehind =
                    intersections.length > 0 &&
                    intersections[0].distance < distance - 45;
                leftOpacity = leftBehind ? 0.5 : 1.0;
            }

            if (rightVisible) {
                const buttonPos = rightButton.position;
                const dir = new THREE.Vector3()
                    .subVectors(buttonPos, camera.position)
                    .normalize();
                raycaster.set(camera.position, dir);
                const distance = camera.position.distanceTo(buttonPos);
                const intersections = raycaster.intersectObjects(
                    occlusionCabinets,
                    true
                );
                const rightBehind =
                    intersections.length > 0 &&
                    intersections[0].distance < distance - 65;
                rightOpacity = rightBehind ? 0.5 : 1.0;
            }
        }

        // Update opacity based on occlusion
        leftButton.material.opacity = leftOpacity;
        rightButton.material.opacity = rightOpacity;

        // Ensure matrices are updated for raycasting
        leftButton.updateMatrixWorld(true);
        rightButton.updateMatrixWorld(true);
        buttonGroup.updateMatrixWorld(true);
    } // End of if (!shouldHideButtons) block

    return {buttonGroup, leftButton, rightButton};
};
